import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useBackupPolling } from '../../hooks/useBackupPolling';
import ReactDOM from 'react-dom';
import { Bell, Clock, AlertTriangle, CheckCircle, User, Utensils, MapPin, Home, Package, Settings, QrCode, Search, DollarSign, Percent, Plus, Trash2, Flag, X, Users, PauseCircle, PlayCircle, ShoppingBag, Volume2 } from 'lucide-react';
import { PickupQueue } from './WaiterPickupQueue.js';
import { subscribeToTickets, getTickets, pickupTicket, deliverItems as storeDeliverItems } from './SharedPickupStore.js';
import io from 'socket.io-client';
import './WaiterDashboard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { clearTable } from '../apiClient';

// Simple static data - to be replaced with backend API calls later
const STATIC_TABLES = [
  {
    id: 1,
    status: 'occupied-paid',
    billTotal: 850.00,
    assignedWaiter: 'Waiter A',
    zone: 'Zone 1',
    lastAction: 'Payment received',
    lastActionTime: new Date(Date.now() - 10 * 60000)
  },
  {
    id: 3,
    status: 'occupied-eating',
    billTotal: 1200.50,
    assignedWaiter: 'Waiter A',
    zone: 'Zone 1',
    lastAction: 'Food served',
    lastActionTime: new Date(Date.now() - 25 * 60000)
  },
  {
    id: 5,
    status: 'occupied-paid',
    billTotal: 650.75,
    assignedWaiter: 'Waiter A',
    zone: 'Zone 1',
    lastAction: 'Payment received',
    lastActionTime: new Date(Date.now() - 5 * 60000)
  }
];

// REMOVED: Static pickup queue data - now loaded from API
// const STATIC_PICKUP_QUEUE = [...] 

const STATIC_CALL_REQUESTS = [
  {
    id: 'call-1',
    table_number: 3,
    request_type: 'assistance',
    created_at: new Date(Date.now() - 2 * 60000),
    status: 'active'
  }
];

const WaiterDashboard = ({ goTo }) => {
  // ⚠️ CRITICAL: Check for businessId first before initializing anything
  const initialBusinessId = React.useMemo(() => {
    try {
      const userDataString = localStorage.getItem('user');
      if (!userDataString) {
        console.error('❌ No user data found in localStorage');
        return null;
      }
      const userData = JSON.parse(userDataString);
      return userData.businessId || userData.business_id || userData.business?.businessId || userData.business?.business_id || null;
    } catch (error) {
      console.error('❌ Error reading user data:', error);
      return null;
    }
  }, []);

  // All hooks must be called at the top level - never conditionally
  const [activeScreen, setActiveScreen] = useState('pickup');
  const [currentWaiter] = useState('Waiter A');
  const [waiterZone] = useState('Zone 1 (Tables 1-6)');

  // Table and task states
  const [tables, setTables] = useState([]);
  const [pickupQueue, setPickupQueue] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [callRequests, setCallRequests] = useState([]);
  const [onDeliveryItems, setOnDeliveryItems] = useState([]);
  const [activeTasks, setActiveTasks] = useState([]);
  const [clearingTables, setClearingTables] = useState(new Set());

  // Chat messages state
  const [chatMessages, setChatMessages] = useState([]); // All chat messages
  const [unreadChatCount, setUnreadChatCount] = useState(0); // Unread message count
  const [expandedChatTable, setExpandedChatTable] = useState(null); // Track which table's chat is expanded
  const chatMessagesEndRef = useRef(null); // Ref for auto-scrolling chat to bottom
  const [tableMessages, setTableMessages] = useState({}); // Store fetched messages by table number
  const [chatReplyText, setChatReplyText] = useState({}); // Store reply text for each table

  // Take Away orders state
  const [takeAwayQueue, setTakeAwayQueue] = useState([]);
  const [removingTakeAwayCards, setRemovingTakeAwayCards] = useState(new Set());

  // WebSocket state
  const [socket, setSocket] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Voice announcement state for waiter calls
  const voiceIntervalRef = useRef(null);
  const hasInitialLoadRef = useRef(false); // Track if initial data load is complete (use ref to avoid re-renders)
  const announcedCallsRef = useRef(new Set()); // Track which calls have been announced

  // UPI Payment confirmation state
  const [pendingPayments, setPendingPayments] = useState({}); // { tableNumber: { amount, timestamp, orderId } }
  const paymentVoiceIntervalsRef = useRef({}); // Track intervals per table: { tableNumber: intervalId }
  const announcedPaymentsRef = useRef(new Set()); // Track which payments have been announced
  const [confirmedPaymentTimestamps, setConfirmedPaymentTimestamps] = useState({}); // { tableNumber: { timestamp, sessionId } }

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);

  // Integrated TableGrid state
  const [timerModal, setTimerModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [tableDetails, setTableDetails] = useState(null); // Real-time table order details
  const [fetchingTableId, setFetchingTableId] = useState(null); // Track which table is being fetched
  const [callTimers, setCallTimers] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [tableOrdersModal, setTableOrdersModal] = useState(null); // { table, orders }
  const tableOrdersAbortRef = useRef(null); // AbortController for order history fetch
  const [waiterOrderHistoryModal, setWaiterOrderHistoryModal] = useState(null); // { ordersByTable, loading, error }

  // Waiter selection state
  const [selectedWaiter, setSelectedWaiter] = useState('');
  const [waiterAssignments, setWaiterAssignments] = useState([]);
  
  // Dashboard toggle state
  const [waiterDashboardEnabled, setWaiterDashboardEnabled] = useState(true); // Default to true
  
  // Payment QR image state
  const [paymentQrImageUrl, setPaymentQrImageUrl] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);

  // Action states
  const [selectedTable, setSelectedTable] = useState(1);
  const [issueDescription, setIssueDescription] = useState('');
  const [statusInfo, setStatusInfo] = useState('');
  const [discountPercent, setDiscountPercent] = useState(10);
  const [discountReason, setDiscountReason] = useState('');
  const [newItem, setNewItem] = useState({ name: '', quantity: 1, price: 0, category: 'Main' });
  const [nowMs, setNowMs] = useState(Date.now());
  const isAnyModalOpen = !!detailModal || !!tableOrdersModal || !!waiterOrderHistoryModal || showServiceModal || showActionsModal || showIssueModal || showStatusModal || showClearModal || showPaymentModal || showDiscountModal || showAddItemModal;

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'

  // Ref to preserve scroll position in modal
  const modalBodyRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isScrollingRef = useRef(false);
  
  // Refs for timer display (direct DOM updates - no React re-renders)
  const timerHoursRef = useRef(null);
  const timerMinutesRef = useRef(null);
  const timerSecondsRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // ===== UTILITY FUNCTIONS =====
  // Show toast notification
  const showToastNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000); // Auto-hide after 2 seconds
  };

  // Get business ID from localStorage
  const getBusinessId = () => {
    try {
      const userDataString = localStorage.getItem('user');
      
      if (!userDataString) {
        console.error('❌ [getBusinessId] No user data in localStorage!');
        console.error('❌ Please log in to continue');
        console.error('📍 Available localStorage keys:', Object.keys(localStorage));
        return null;
      }
      
      const userData = JSON.parse(userDataString);
      
      // Check multiple possible locations for businessId
      const businessId = 
        userData.businessId || 
        userData.business_id || 
        userData.business?.businessId || 
        userData.business?.business_id;
      
      if (!businessId) {
        console.error('❌ [getBusinessId] businessId not found in user data!');
        console.error('❌ User object:', userData);
        console.error('❌ Checked: userData.businessId, userData.business_id, userData.business.businessId');
        return null;
      }
      
      return businessId;
    } catch (error) {
      console.error('❌ [getBusinessId] Error parsing user data:', error);
      return null;
    }
  };

  // Resolve API and Socket base URLs
  // ✅ FIX: Waiter Dashboard MUST connect to QRinv backend (port 5000) to receive order broadcasts
  // The broadcast-order endpoint emits to business:${businessId}:waiter room on port 5000
  const resolveBaseUrl = (rawUrl) => {
    try {
      const url = new URL(rawUrl);
      const host = window?.location?.hostname;
      if (host && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')) {
        url.hostname = host;
      }
      return url.toString().replace(/\/+$/, '');
    } catch {
      return rawUrl.replace(/\/+$/, '');
    }
  };

  const API_BASE = resolveBaseUrl(process.env.REACT_APP_API_URL || 'http://localhost:5000/api');
  
  // ✅ FIX: WebSocket MUST connect to QRinv backend (port 5000) for dashboard updates
  // - Owner Dashboard connects to port 5000
  // - Waiter Dashboard must also connect to port 5000 to receive same events
  // - broadcast-order and notify-payment endpoints emit on port 5000
  const SOCKET_BASE = resolveBaseUrl(
    process.env.REACT_APP_SOCKET_URL ||
    process.env.REACT_APP_WEBSOCKET_URL ||
    (process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
      : 'http://localhost:5000') // Changed from 5001 to 5000
  );
  
  // Fetch dashboard toggle state and payment QR image
  useEffect(() => {
    const fetchToggleState = async () => {
      try {
        const businessId = getBusinessId();
        const response = await fetch(
          `${API_BASE}/qr-billing/dashboard-toggles?businessId=${businessId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Business-ID': businessId
            }
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.toggles) {
            setWaiterDashboardEnabled(result.toggles.waiterEnabled || false);
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to load dashboard toggle state:', error);
        // Default to enabled if fetch fails
        setWaiterDashboardEnabled(true);
      }
    };
    
    const fetchPaymentQrImage = async () => {
      try {
        const businessId = getBusinessId();
        const response = await fetch(
          `${API_BASE}/qr-billing/payment-qr-image?businessId=${businessId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Business-ID': businessId
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.qrImageUrl) {
            setPaymentQrImageUrl(`${API_BASE.replace('/api', '')}${data.qrImageUrl}`);
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to load payment QR image:', error);
      }
    };

    fetchToggleState();
    fetchPaymentQrImage();
  }, [API_BASE]);

  // Force re-render every 5 seconds to update table colors based on confirmed payment timestamps
  useEffect(() => {
    const interval = setInterval(() => {
      // Check if any confirmed payments have expired (>2 minutes)
      const now = Date.now();
      const hasExpiredPayments = Object.entries(confirmedPaymentTimestamps).some(([_, data]) => {
        return now - data.timestamp >= 120000;
      });
      
      if (hasExpiredPayments) {
        // Force state update to trigger re-render
        setConfirmedPaymentTimestamps(prev => ({ ...prev }));
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(interval);
  }, [confirmedPaymentTimestamps]);

  // Fetch pickup queue from backend
  const fetchPickupQueue = async () => {
    if (isAnyModalOpen) { return; }
    try {
      const businessId = getBusinessId();
      if (!businessId) {
        console.error('❌ Cannot fetch pickup queue: businessId is null/undefined');
        return;
      }

      const response = await fetch(
        `${API_BASE}/waiter/pickup-queue?businessId=${businessId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch pickup queue: ${response.statusText}`);
      }

      const data = await response.json();

      // Convert date strings to Date objects for timer calculations
      const allTickets = (data.tickets || []).map(ticket => ({
        ...ticket,
        placedAt: ticket.orderedAt ? new Date(ticket.orderedAt) : null,
        orderedAt: ticket.orderedAt ? new Date(ticket.orderedAt) : null,
        completedAt: ticket.completedAt ? new Date(ticket.completedAt) : null
      }));

      // ✅ Filter out items older than 24 hours (1440 minutes)
      const now = new Date();
      const filteredTickets = allTickets.filter(ticket => {
        if (!ticket.orderedAt) return true; // Keep if no timestamp
        const diffMinutes = Math.floor((now - ticket.orderedAt) / 60000);
        return diffMinutes < 1440; // 24 hours = 1440 minutes
      });

      const ready = filteredTickets.filter(t => t.status === 'ready_on_pass');
      const inHand = filteredTickets.filter(t => t.status === 'in_hand');

      setPickupQueue(ready);
      setOnDeliveryItems(inHand);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('❌ Error fetching pickup queue:', err);
    }
  };

  // Fetch waiter call requests from backend
  const fetchWaiterCalls = async () => {
    if (isAnyModalOpen) { return; }
    try {
      const businessId = getBusinessId();
      if (!businessId) {
        console.error('❌ Cannot fetch waiter calls: businessId is null/undefined');
        return;
      }

      const response = await fetch(
        `${API_BASE}/waiter-calls/${businessId}?status=pending`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch waiter calls: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.requests && data.requests.length > 0) {
        data.requests.forEach((req, idx) => {
        });
      }

      setCallRequests(data.requests || []);

      // Mark initial load complete AFTER setting call requests (prevents bell on dashboard open)
      if (!hasInitialLoadRef.current) {
        hasInitialLoadRef.current = true;
      }
    } catch (err) {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ ERROR FETCHING WAITER CALLS');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('Error:', err);
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
  };

  // Fetch chat messages from backend
  const fetchChatMessages = async () => {
    if (isAnyModalOpen) { return; }
    try {
      const businessId = getBusinessId();

      // Fetch ALL chat messages grouped by table (not just unread)
      const response = await fetch(
        `${API_BASE}/chat/all-tables/${businessId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        // Set all chat messages by table
        const allMessages = data.tableMessages || [];
        setChatMessages(allMessages);

        // Calculate total unread count
        const totalUnread = allMessages.reduce((sum, item) => sum + parseInt(item.unread_count || 0), 0);
        setUnreadChatCount(totalUnread);

      }
    } catch (err) {
      console.error('❌ Error fetching chat messages:', err);
    }
  };

  // Fetch take away queue from backend
  const fetchTakeAwayQueue = async () => {
    if (isAnyModalOpen) { return; }
    try {
      const businessId = getBusinessId();

      const response = await fetch(
        `${API_BASE}/waiter/takeaway-queue?businessId=${businessId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        }
      );

      if (!response.ok) {
        console.error(`❌ Failed to fetch takeaway queue: ${response.status} ${response.statusText}`);
        throw new Error(`Failed to fetch takeaway queue: ${response.statusText}`);
      }

      const data = await response.json();

      // Convert date strings to Date objects for timer calculations
      const tickets = (data.tickets || []).map(ticket => {
        // Parse the timestamp - PostgreSQL returns it in UTC
        let orderedAt = null;
        if (ticket.orderedAt) {
          // Create a Date object from the timestamp
          orderedAt = new Date(ticket.orderedAt);

          // The timestamp from DB is in UTC, but JavaScript Date already handles this correctly
          // No need to adjust - Date.getTime() gives us milliseconds since epoch which is timezone-agnostic
        }

        return {
          ...ticket,
          orderedAt
        };
      });

      setTakeAwayQueue(tickets);
    } catch (err) {
      console.error('❌ Error fetching take away queue:', err);
      console.error('❌ Error details:', err.message);
    }
  };

  // Fetch tables from backend
  const fetchTables = async () => {
    if (isAnyModalOpen) { 
      return; 
    }
    
    try {
      const businessId = getBusinessId();
      if (!businessId) {
        console.error('❌ Cannot fetch tables: businessId is null/undefined');
        return;
      }

      const response = await fetch(
        `${API_BASE}/waiter/tables?businessId=${businessId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch tables: ${response.statusText}`);
      }

      const data = await response.json();

      // Convert date strings to Date objects and ensure timeMs is available
      const tablesWithDates = (data.tables || []).map(table => ({
        ...table,
        lastActionTime: table.lastActionTime ? new Date(table.lastActionTime) : null,
        startedAt: table.startedAt ? new Date(table.startedAt) : null,
        // Keep timeMs from backend, recalculate on render for real-time updates
        timeMs: table.timeMs || 0
      }));

      // ✅ Filter out tables with last action older than 24 hours (1440 minutes)
      const now = new Date();
      const filteredTables = tablesWithDates.filter(table => {
        if (!table.lastActionTime) return true; // Keep if no timestamp
        const diffMinutes = Math.floor((now - table.lastActionTime) / 60000);
        return diffMinutes < 1440; // 24 hours = 1440 minutes
      });

      setTables(filteredTables);
      
      // Clear confirmed payment timestamps ONLY when session ID changes (new customer)
      setConfirmedPaymentTimestamps(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        tablesWithDates.forEach(table => {
          const confirmedData = updated[table.id];
          if (confirmedData) {
            // Check if session ID changed - indicates new customer
            if (table.sessionId && table.sessionId !== confirmedData.sessionId) {
              delete updated[table.id];
              hasChanges = true;
            }
          }
        });
        
        return hasChanges ? updated : prev;
      });
    } catch (err) {
      console.error('❌ Error fetching tables:', err);
    }
  };

  // Voice announcement for waiter calls
  const announceWaiterCall = (tableNumber) => {
    try {
      if (!window.speechSynthesis) {
        console.warn('🔇 Speech synthesis not supported');
        return;
      }

      const announcement = `Table ${tableNumber} requested`;

      window.speechSynthesis.cancel(); // Cancel any ongoing speech
      
      const utterance = new SpeechSynthesisUtterance(announcement);
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      utterance.lang = 'en-IN';
      
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        const indianVoice = voices.find(voice => 
          voice.lang.includes('en-IN') || voice.lang.includes('en-GB') || voice.lang.includes('en-US')
        );
        if (indianVoice) {
          utterance.voice = indianVoice;
        }
      }
      
      // Add event handlers for debugging
      utterance.onstart = () => {};
      utterance.onerror = (e) => console.error('❌ Waiter call announcement error:', e);
      utterance.onend = () => {};
      
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      console.error('❌ Error with voice announcement:', err);
    }
  };

  // Voice announcement for payment confirmation (loops until confirmed)
  const announcePaymentPending = (tableNumber) => {
    try {
      if (!window.speechSynthesis) {
        console.warn('🔇 Speech synthesis not supported');
        return;
      }

      const key = `payment-${tableNumber}`;
      
      // If already announced, skip
      if (announcedPaymentsRef.current.has(key)) {
        return;
      }

      // Mark as announced
      announcedPaymentsRef.current.add(key);
      // Clear any existing payment voice interval for THIS table
      if (paymentVoiceIntervalsRef.current[tableNumber]) {
        clearInterval(paymentVoiceIntervalsRef.current[tableNumber]);
        delete paymentVoiceIntervalsRef.current[tableNumber];
      }

      // Create looping voice announcement
      const speak = () => {
        window.speechSynthesis.cancel(); // Cancel previous
        
        const utterance = new SpeechSynthesisUtterance(`Confirm payment for table ${tableNumber}`);
        utterance.rate = 0.9;
        utterance.pitch = 1.2; // Higher pitch for urgency
        utterance.volume = 1.0;
        utterance.lang = 'en-IN';
        
        const voices = window.speechSynthesis.getVoices();
        
        if (voices.length > 0) {
          // Try to get a female voice - check multiple patterns
          const femaleVoice = voices.find(voice => {
            const name = voice.name.toLowerCase();
            const lang = voice.lang.toLowerCase();
            const isFemale = name.includes('female') || 
                           name.includes('zira') || 
                           name.includes('hazel') ||
                           name.includes('samantha') || 
                           name.includes('victoria') ||
                           name.includes('karen') ||
                           name.includes('moira') ||
                           name.includes('tessa') ||
                           name.includes('fiona') ||
                           name.includes('kate') ||
                           name.includes('google uk english female') ||
                           name.includes('microsoft zira');
            const isEnglish = lang.includes('en');
            return isFemale && isEnglish;
          });
          
          // Fallback: any female-sounding voice
          const anyFemaleVoice = femaleVoice || voices.find(v => 
            v.name.toLowerCase().includes('female')
          );
          
          // Use the female voice or default
          if (anyFemaleVoice) {
            utterance.voice = anyFemaleVoice;
          }
        }
        
        utterance.onerror = (e) => console.error('❌ Payment announcement error:', e);
        utterance.onend = () => {};
        
        window.speechSynthesis.speak(utterance);
      };

      // Speak immediately
      speak();

      // Repeat every 8 seconds until confirmed - store interval for THIS table
      paymentVoiceIntervalsRef.current[tableNumber] = setInterval(speak, 8000);
    } catch (err) {
      console.error('❌ Error with payment voice announcement:', err);
    }
  };

  // Clear pending payment state and voice loop for a table
  const clearPendingPayment = (tableNumber, reason = 'unknown') => {
    if (tableNumber === null || tableNumber === undefined) {
      return;
    }

    const tableKey = String(tableNumber);

    // Stop voice announcement for this table if running
    const intervalId = paymentVoiceIntervalsRef.current[tableKey];
    if (intervalId) {
      clearInterval(intervalId);
      delete paymentVoiceIntervalsRef.current[tableKey];
    }

    // Remove from announced set
    announcedPaymentsRef.current.delete(`payment-${tableKey}`);

    // Remove pending payment state
    setPendingPayments(prev => {
      if (!prev[tableKey]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[tableKey];
      return updated;
    });
  };

  // Filter waiter calls for the selected waiter's assigned tables
  const getWaiterCalls = () => {
    if (!selectedWaiter || waiterAssignments.length === 0) {
      // If no waiter selected, return all calls
      return callRequests;
    }

    const assignment = waiterAssignments.find(w => w.waiterName === selectedWaiter);
    if (!assignment) {
      return callRequests;
    }

    // Filter calls by assigned table range
    return callRequests.filter(call => {
      const tableNum = parseInt(call.table_number);
      
      if (isNaN(tableNum) || tableNum === 0) {
        return false;
      }

      const isInRange = tableNum >= assignment.fromTable && tableNum <= assignment.toTable;
      
      return isInRange;
    });
  };

  // Start continuous voice announcements for waiter calls (filtered by assignment)
  const startVoiceAnnouncements = () => {
    // Stop any existing announcements first
    stopVoiceAnnouncements();

    const myCallRequests = getWaiterCalls();

    if (myCallRequests.length === 0) {
      return;
    }

    // Announce all pending calls immediately
    myCallRequests.forEach(call => {
      if (call.status === 'pending') {
        announceWaiterCall(call.table_number);
      }
    });

    // Then repeat every 5 seconds
    voiceIntervalRef.current = setInterval(() => {
      const currentMyCallRequests = getWaiterCalls();
      currentMyCallRequests.forEach(call => {
        if (call.status === 'pending') {
          announceWaiterCall(call.table_number);
        }
      });
    }, 5000); // Repeat every 5 seconds
  };

  // Stop continuous voice announcements
  const stopVoiceAnnouncements = () => {
    if (voiceIntervalRef.current) {
      clearInterval(voiceIntervalRef.current);
      voiceIntervalRef.current = null;
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  // Fetch detailed table information with order items
  const fetchTableDetails = async (tableNumber) => {
    try {
      setFetchingTableId(tableNumber);
      const businessId = getBusinessId();

      const response = await fetch(
        `${API_BASE}/waiter/table/${tableNumber}?businessId=${businessId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch table details: ${response.statusText}`);
      }

      const data = await response.json();

      setTableDetails(data.table);
      setFetchingTableId(null);
      return data.table;
    } catch (err) {
      console.error('❌ Error fetching table details:', err);
      setFetchingTableId(null);
      return null;
    }
  };

  // ===== ALL USEEFFECT HOOKS MUST BE AT TOP LEVEL =====
  // Initialize WebSocket connection
  useEffect(() => {
    const businessId = getBusinessId();
    if (!businessId) {
      console.error('❌ No business ID found');
      return;
    }

    const newSocket = io(SOCKET_BASE, {
      auth: {
        token: localStorage.getItem('token')
      },
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      // Register with backend as both waiter AND owner to be tracked in "Connected Owners"
      newSocket.emit('waiter:register', businessId);
      newSocket.emit('owner:register', { businessId });
    });

    // Confirm waiter registration
    newSocket.on('waiter:registered', (data) => {
    });

    // Confirm owner registration (for dashboard updates tracking)
    newSocket.on('dashboard:initial', (data) => {
    });

    // Listen for dashboard updates (table status changes)
    newSocket.on('dashboard:update', (data) => {
      // Avoid background reflow while modal is open
      if (!detailModal) {
        fetchTables();
      } else {
      }
    });

    // Listen for new orders (triggers table color change to yellow)
    newSocket.on('order:new', (data) => {
      if (!detailModal) {
        fetchTables();
      }
    });

    // Listen for payment completed (triggers table color change to green)
    newSocket.on('payment:completed', (data) => {
      if (data?.tableNumber !== undefined && data?.tableNumber !== null) {
        clearPendingPayment(data.tableNumber, 'payment:completed');
      }
      if (!detailModal) {
        fetchTables();
      }
    });

    // Listen for payment confirmed (EAT-FIRST: triggers immediate green status)
    newSocket.on('payment:confirmed', (data) => {
      // Set timestamp for tracking green→ash transition
      if (data.tableNumber) {
        const tableNum = String(data.tableNumber);
        clearPendingPayment(tableNum, 'payment:confirmed');
        setConfirmedPaymentTimestamps(prev => ({
          ...prev,
          [tableNum]: { 
            timestamp: Date.now(), 
            sessionId: data.sessionId || null 
          }
        }));
      }
      
      // Immediately refresh tables to show green status
      if (!detailModal) {
        fetchTables();
      }
    });

    newSocket.on('waiter:order_ready', (data) => {
      console.warn('🚨 WAITER ALERT: New order ready! 🚨');

      // Refresh pickup queue and tables to get latest data
      fetchPickupQueue();
      fetchTables();
      fetchTakeAwayQueue(); // ✅ Also refresh take away queue

      // Optional: Show toast notification
      // showNotification(`Table ${data.tableNumber} - Order ${data.orderNumber} ready!`);
    });

    newSocket.on('waiter:order_picked_up', (data) => {
      fetchPickupQueue();
      fetchTables();
      fetchTakeAwayQueue(); // ✅ Also refresh take away queue
    });

    newSocket.on('waiter:order_delivered', (data) => {
      fetchPickupQueue();
      fetchTables();
      fetchTakeAwayQueue(); // ✅ Also refresh take away queue
    });

    newSocket.on('waiter_call_request', (data) => {
      console.warn(`🚨 TABLE ${data.tableNumber} IS CALLING! 🚨`);

      // Check if this table belongs to the current waiter before announcing
      if (selectedWaiter && waiterAssignments.length > 0) {
        const assignment = waiterAssignments.find(w => w.waiterName === selectedWaiter);
        if (assignment) {
          const tableNum = parseInt(data.tableNumber);
          const isMyTable = tableNum >= assignment.fromTable && tableNum <= assignment.toTable;
          
          if (isMyTable) {
            announceWaiterCall(data.tableNumber);
          }
        }
      } else {
        // No waiter selected, announce all calls
        announceWaiterCall(data.tableNumber);
      }

      // Refresh waiter calls list
      fetchWaiterCalls();

      // Optional: Show toast notification
      // showNotification(`Table ${data.tableNumber} is calling!`);
    });

    // ✅ Listen for takeaway order updates
    newSocket.on('takeaway_order_update', (data) => {
      // Refresh takeaway queue
      fetchTakeAwayQueue();

      // Also refresh pickup queue in case it affects that view
      fetchPickupQueue();
    });

    // ✅ Listen for chat messages
    newSocket.on('chat_message', (data) => {
      try {
        // ✅ INSTANT UPDATE: Update tableMessages directly for real-time display
        if (data.messageData) {
          // Create message object in the format expected by the UI
          const newMessage = {
            id: data.messageData.id || data.messageData.messageId || Date.now(),
            business_id: data.businessId,
            table_number: data.tableNumber,
            sender_type: data.messageData.senderType,
            sender_name: data.messageData.senderName,
            message: data.messageData.message,
            message_type: data.messageData.messageType || 'text',
            is_read: false,
            created_at: data.messageData.createdAt || data.messageData.timestamp || new Date().toISOString()
          };

          // Add the new message to the table's messages INSTANTLY (works even if not expanded)
          setTableMessages(prev => {
            const existingMessages = prev[data.tableNumber] || [];

            // Check if message already exists (avoid duplicates)
            // Priority: Check by database ID first (most reliable)
            const existsById = newMessage.id && existingMessages.some(msg => msg.id === newMessage.id);

            // Secondary check: Same content from same sender within 5 seconds
            const existsByContent = existingMessages.some(msg =>
              msg.message === newMessage.message &&
              msg.sender_type === newMessage.sender_type &&
              Math.abs(new Date(msg.created_at) - new Date(newMessage.created_at)) < 5000
            );

            if (existsById || existsByContent) {
              return prev;
            }

            const updated = {
              ...prev,
              [data.tableNumber]: [...existingMessages, newMessage]
            };
            return updated;
          });
        }

        // Bell sound removed - only plays for waiter call requests
      } catch (error) {
        console.error('❌ Error processing chat message:', error);
      }
    });

    // ✅ Listen for customer payment initiated (UPI QR payments)
    newSocket.on('customer:payment-initiated', (data) => {
      // Store pending payment for this table
      setPendingPayments(prev => {
        const updated = {
          ...prev,
          [data.tableNumber]: {
            amount: data.amount,
            timestamp: data.timestamp,
            orderId: data.orderId,
            businessId: data.businessId
          }
        };
        return updated;
      });
      // Start voice announcement for payment pending (only pass tableNumber)
      try {
        announcePaymentPending(data.tableNumber);
      } catch (error) {
        console.error('❌ [VOICE] announcePaymentPending failed:', error);
      }
      // Refresh tables to show orange color
      try {
        fetchTables();
      } catch (error) {
        console.error('❌ [FETCH] fetchTables failed:', error);
      }
    });

    newSocket.on('disconnect', (reason) => {
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ WAITER DASHBOARD - WEBSOCKET CONNECTION ERROR');
      console.error('🚨 Error:', error);
      console.error('📡 Trying to connect to:', SOCKET_BASE);
      console.error('🌐 Error message:', error.message);
      console.error('🔍 Error type:', error.type);
      console.error('📋 Full error:', error);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [detailModal]);

  // Initial load and periodic refresh of pickup queue and tables
  useEffect(() => {
    fetchPickupQueue();
    fetchTables();
    fetchWaiterCalls(); // ✅ Added: Fetch waiter call requests
    fetchChatMessages(); // ✅ Added: Fetch chat messages
    fetchTakeAwayQueue(); // ✅ Added: Fetch take away queue

    // Load waiter assignments from backend API (with localStorage fallback)
    const loadWaiterAssignments = async () => {
      const businessId = getBusinessId();
      if (!businessId) return;

      try {
        const response = await fetch(
          `${API_BASE}/waiter-assignments/${businessId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Business-ID': businessId
            }
          }
        );

        if (response.ok) {
          const result = await response.json();

          if (result.success && result.assignments.length > 0) {
            setWaiterAssignments(result.assignments);

            // Set first waiter as default if not already selected
            if (!selectedWaiter && result.assignments.length > 0) {
              setSelectedWaiter(result.assignments[0].waiterName);
            }

            // Update localStorage as cache
            localStorage.setItem(`waiterAssignments_${businessId}`, JSON.stringify(result.assignments));
            return;
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to load from backend, using localStorage:', error);
      }

      // Fallback to localStorage if backend fails
      const savedAssignments = localStorage.getItem(`waiterAssignments_${businessId}`);
      if (savedAssignments) {
        const assignments = JSON.parse(savedAssignments);
        setWaiterAssignments(assignments);
        // Set first waiter as default if not already selected
        if (!selectedWaiter && assignments.length > 0) {
          setSelectedWaiter(assignments[0].waiterName);
        }
      }
    };

    loadWaiterAssignments();

  }, [isAnyModalOpen]);

  // Backup refresh behind the WebSocket, which already pushes queue and table
  // changes. At 5s this fired three API calls a tick, all day and all night,
  // which was a large share of what kept the Neon compute awake. A waiter tablet
  // is rarely put to sleep, so "tab hidden" was never the real quiet signal — the
  // poll now stops when the screen goes untouched and resumes on the first tap.
  useBackupPolling(() => {
    // Skip periodic refresh while a modal is visible to prevent flicker
    if (isAnyModalOpen) return;
    fetchPickupQueue();
    fetchTables();
    fetchTakeAwayQueue(); // ✅ Also refresh take away queue
  }, 20000);

  // ===== EXISTING USEEFFECT HOOKS =====
  // Manage voice announcements when callRequests change or waiter changes
  useEffect(() => {
    const myCallRequests = getWaiterCalls();
    const pendingCalls = myCallRequests.filter(req => req.status === 'pending');
    
    if (pendingCalls.length > 0) {
      startVoiceAnnouncements();
    } else {
      stopVoiceAnnouncements();
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      stopVoiceAnnouncements();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callRequests, selectedWaiter]);

  // DEBUG: Log callRequests whenever they change
  useEffect(() => {
  }, [callRequests]);

  // Start timers for new calls, and clear timers when calls disappear
  useEffect(() => {
    // Skip if modal is open to prevent re-renders
    if (isAnyModalOpen) return;
    
    const activeSet = new Set(callRequests.map(r => r.table_number));
    setCallTimers(prev => {
      const updated = { ...prev };
      // Start timers for newly active tables
      activeSet.forEach(tn => {
        const key = String(tn);
        if (!updated[key]) {
          updated[key] = { start: Date.now(), running: true, elapsedMs: 0 };
        } else if (!updated[key].running && updated[key].elapsedMs === 0) {
          // ensure fresh start if previously reset
          updated[key] = { start: Date.now(), running: true, elapsedMs: 0 };
        }
      });
      // Remove timers for tables no longer calling
      Object.keys(updated).forEach(k => {
        if (!activeSet.has(Number(k))) {
          delete updated[k];
        }
      });
      return updated;
    });
  }, [callRequests, isAnyModalOpen]);

  // Tick running timers once per second - ONLY update state when modal is closed
  useEffect(() => {
    const id = setInterval(() => {
      // Only trigger React re-render if modal is NOT open (prevents glitching)
      if (!detailModal) {
        setNowMs(Date.now());
        
        // Only update call timers when modal is closed
        setCallTimers(prev => {
          const updated = { ...prev };
          let changed = false;
          Object.keys(updated).forEach(k => {
            const t = updated[k];
            if (t.running) {
              updated[k] = { ...t, elapsedMs: Date.now() - t.start };
              changed = true;
            }
          });
          return changed ? { ...updated } : prev;
        });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [detailModal]);

  // Direct DOM timer update - NO React re-renders (prevents glitching)
  useEffect(() => {
    if (!detailModal) {
      // Clear interval when modal is closed
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    // Update timer DOM directly every second without React re-renders
    const updateTimerDisplay = () => {
      if (timerHoursRef.current && timerMinutesRef.current && timerSecondsRef.current) {
        const [h, m, s] = splitHMS(getTimeSpentMs(detailModal.table));
        const pad = (n) => String(n).padStart(2, '0');
        timerHoursRef.current.textContent = pad(h);
        timerMinutesRef.current.textContent = pad(m);
        timerSecondsRef.current.textContent = pad(s);
      }
    };

    // Initial update
    updateTimerDisplay();

    // Update every second
    timerIntervalRef.current = setInterval(updateTimerDisplay, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [detailModal?.table?.id]); // Only re-run when modal opens/closes or table changes, NOT on tableDetails updates

  // ULTRA-AGGRESSIVE scroll position lock using useLayoutEffect (runs BEFORE paint)
  React.useLayoutEffect(() => {
    if (!modalBodyRef.current || !detailModal) return;

    const modalBody = modalBodyRef.current;

    // Restore scroll position IMMEDIATELY (before browser paints)
    if (scrollPositionRef.current > 0 && !isScrollingRef.current) {
      modalBody.scrollTop = scrollPositionRef.current;
    }

    // Save scroll position on every scroll event
    const handleScroll = (e) => {
      isScrollingRef.current = true;
      scrollPositionRef.current = modalBody.scrollTop;
      
      // Clear the scrolling flag after a brief delay
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 100);
    };

    modalBody.addEventListener('scroll', handleScroll, { passive: true });

    // Also save on any interaction
    const saveScroll = () => {
      scrollPositionRef.current = modalBody.scrollTop;
    };

    modalBody.addEventListener('touchmove', saveScroll, { passive: true });
    modalBody.addEventListener('wheel', saveScroll, { passive: true });

    return () => {
      modalBody.removeEventListener('scroll', handleScroll);
      modalBody.removeEventListener('touchmove', saveScroll);
      modalBody.removeEventListener('wheel', saveScroll);
    };
  }); // Run on EVERY render to restore scroll immediately

  // Reset scroll position when modal closes
  React.useEffect(() => {
    if (!detailModal) {
      scrollPositionRef.current = 0;
      isScrollingRef.current = false;
    }
  }, [detailModal]);

  // Apply waiter-only body background class while this dashboard is mounted
  useEffect(() => {
    try {
      const body = document.body;
      const html = document.documentElement;
      body.classList.add('waiter-app-active');
      html.classList.add('waiter-app-active');
      const prevBodyBg = body.style.backgroundColor;
      body.style.backgroundColor = '#000000';
      const rootEl = document.getElementById('root');
      const prevRootBg = rootEl ? rootEl.style.backgroundColor : undefined;
      if (rootEl) rootEl.style.backgroundColor = '#000000';
      const appEl = document.querySelector('.App');
      const prevAppBg = appEl ? appEl.style.backgroundColor : undefined;
      if (appEl) appEl.style.backgroundColor = '#000000';

      return () => {
        try {
          body.classList.remove('waiter-app-active');
          html.classList.remove('waiter-app-active');
          body.style.backgroundColor = prevBodyBg || '';
          if (rootEl) rootEl.style.backgroundColor = prevRootBg || '';
          if (appEl) appEl.style.backgroundColor = prevAppBg || '';
        } catch (_) { }
      };
    } catch (_) {
      return () => { };
    }
  }, []);

  // Freeze body scroll while main table detail modal is open to avoid background reflow/flicker
  useEffect(() => {
    const body = document.body;
    if (detailModal) {
      body.classList.add('wd-modal-open');
    } else {
      body.classList.remove('wd-modal-open');
    }
    return () => body.classList.remove('wd-modal-open');
  }, [detailModal]);

  // Initialize with static data (call requests only - tables now loaded from API)
  // NOTE: Pickup queue and tables now loaded from API via fetchPickupQueue() and fetchTables()
  useEffect(() => {
    // Don't set static call requests - let fetchWaiterCalls() handle it from database
    // setCallRequests(STATIC_CALL_REQUESTS);
    setServiceRequests([]);

    // OLD: Used SharedPickupStore (static in-memory data)
    // NOW: Using real-time API + WebSocket
    /*
    const computeLists = (tickets) => {
      const active = tickets.filter(t => {
        const activeItems = (t.items || []).filter(i => !i.id.includes('-served-'));
        return activeItems.some(i => i.status !== 'served' && i.quantity > 0);
      });
      const onDelivery = active.filter(t => {
        const activeItems = (t.items || []).filter(i => !i.id.includes('-served-'));
        return activeItems.some(i => i.status === 'in_hand');
      });
      const ready = active.filter(t => {
        const activeItems = (t.items || []).filter(i => !i.id.includes('-served-'));
        return activeItems.some(i => i.status === 'ready_on_pass') && !activeItems.some(i => i.status === 'in_hand');
      });
      setOnDeliveryItems(onDelivery);
      setPickupQueue(ready);
    };

    computeLists(getTickets());
    const unsubscribe = subscribeToTickets((tkts) => computeLists(tkts));
    return unsubscribe;
    */
  }, []);

  // Service request escalation check
  useEffect(() => {
    const checkEscalation = () => {
      // Skip if modal is open to prevent re-renders
      if (isAnyModalOpen) return;
      
      const now = new Date();
      setServiceRequests(prev =>
        prev.map(request => {
          if (request.status === 'pending' && !request.escalated) {
            const timeDiff = now.getTime() - request.requestedAt.getTime();
            if (timeDiff > 30000) {
              return { ...request, escalated: true, priority: 'high' };
            }
          }
          return request;
        })
      );
    };

    const interval = setInterval(checkEscalation, 5000);
    return () => clearInterval(interval);
  }, [isAnyModalOpen]);

  useEffect(() => {
    // Skip if modal is open to prevent re-renders
    if (isAnyModalOpen) return;
    
    setTables(prev => prev.map(table => ({
      ...table,
      hasServiceRequest: serviceRequests.some(req =>
        req.tableNumber === table.id && req.status === 'pending'
      )
    })));
  }, [serviceRequests, isAnyModalOpen]);

  // Debug: Log takeAwayQueue changes
  useEffect(() => {
  }, [takeAwayQueue]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (expandedChatTable && tableMessages[expandedChatTable]) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        const chatContainer = document.getElementById(`chat-messages-${expandedChatTable}`);
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      }, 100);
    }
  }, [tableMessages, expandedChatTable]);

  // Chat list (unread counts). Live messages arrive on the 'chat_message' socket
  // event, so this is only a catch-up for a dropped socket — no reason for it to
  // keep querying while the screen sits untouched.
  useBackupPolling(() => {
    if (isAnyModalOpen) return; // avoid re-render during modal
    fetchChatMessages();
  }, 30000);

  // ===== INTEGRATED TABLEGRID FUNCTIONALITY =====

  const formatElapsed = (ms = 0) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const s = totalSec % 60;
    const m = Math.floor(totalSec / 60) % 60;
    const h = Math.floor(totalSec / 3600);
    if (h > 0) {
      return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
    }
    if (m > 0) {
      return `${m}m ${String(s).padStart(2, '0')}s`;
    }
    return `${s}s`;
  };

  // Check if a table has an active call request
  const hasActiveCall = (tableNumber) => {
    const hasCall = callRequests.some(req => req.table_number === tableNumber);
    return hasCall;
  };

  // Acknowledge waiter call request
  const acknowledgeWaiterCall = async (tableNumber) => {
    try {
      const businessId = getBusinessId();

      // Find the call request for this table
      const callRequest = callRequests.find(req => req.table_number === tableNumber && req.status === 'pending');

      if (!callRequest) {
        console.warn('⚠️ No pending call request found for this table');
        return;
      }

      // Send acknowledge request to backend FIRST (don't fire and forget)
      const response = await fetch(
        `${API_BASE}/waiter-calls/${callRequest.id}/acknowledge`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'X-Business-ID': businessId
          },
          body: JSON.stringify({
            waiterName: selectedWaiter || currentWaiter
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to acknowledge: ${response.statusText}`);
      }

      // Only after successful backend update, update UI
      setCallRequests(prev => {
        const updated = prev.filter(req => req.id !== callRequest.id);
        return updated;
      });

      // Stop the continuous voice announcements
      stopVoiceAnnouncements();

      // Close the modal
      setDetailModal(null);

      // Show success toast
      showToastNotification(`Call from Table ${tableNumber} accepted!`, 'success');

    } catch (err) {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ ERROR ACKNOWLEDGING WAITER CALL');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('Error:', err);
      console.error('Error message:', err.message);
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      showToastNotification(`Failed to acknowledge call: ${err.message}`, 'error');
    }
  };

  const openTimer = (tableId) => {
    // TODO: Replace with API call - await fetchTableTimerState({ tableNumber: tableId })
    setTimerModal({
      tableId,
      loading: false,
      state: { is_paused: true, paused_at: new Date().toISOString() },
      error: null
    });
  };

  const getPaymentMethodDisplay = (method) => {
    if (!method) return 'Not Specified';
    const methodLower = method.toLowerCase();
    if (methodLower === 'online') return 'UPI/Online';
    if (methodLower === 'card') return 'Card';
    if (methodLower === 'cash') return 'Cash';
    return method; // Return as-is if unknown
  };

  const actStop = () => {
    if (!timerModal) return;
    // TODO: Replace with API call - await stopTableTimer({ tableNumber: timerModal.tableId })
    setTimerModal(m => ({
      ...m,
      loading: false,
      state: { is_paused: true, paused_at: new Date().toISOString() },
      error: null
    }));
  };

  const actClearTable = async () => {
    if (!timerModal) return;
    const tableId = timerModal.tableId;
    setSelectedTable(tableId);
    setTimerModal(null);
    setShowClearModal(true);
  };

  const getTableColorClass = (status, tableNumber) => {
    // Check if table has pending payment (takes priority over other statuses)
    if (tableNumber && pendingPayments[tableNumber]) {
      return 'tg-payment-pending'; // ORANGE - payment pending confirmation
    }

    // ✅ FIX: For pay-first model, rely ONLY on backend status, not confirmedPaymentTimestamps
    // Backend determines if items are served/ready (status = 'occupied-paid')
    // Don't override backend status with frontend timestamp tracking
    
    // For eat-first model only: Check if payment was recently confirmed (within 2 minutes)
    // This is for the eat-first flow where green shows for 2 min after payment, then ash
    if (tableNumber && confirmedPaymentTimestamps[tableNumber]) {
      const confirmData = confirmedPaymentTimestamps[tableNumber];
      const timeSinceConfirmation = Date.now() - confirmData.timestamp;
      if (timeSinceConfirmation >= 120000) {
        // More than 2 minutes after payment - turn ash
        return 'tg-available'; // AVAILABLE - solid ash color, waiting for next customer
      }
      // Less than 2 minutes - let the backend status determine the color
      // Don't force green here - let status drive the color
    }

    switch (status) {
      case 'available':
        return 'tg-available'; // Should not appear in waiter dashboard
      case 'occupied-eating':
        return 'tg-eating'; // YELLOW - QR scanned (seated) OR has unpaid orders OR paid but not served
      case 'occupied-paid':
        return 'tg-paid'; // GREEN - all orders paid AND at least one item served
      case 'reserved':
        return 'tg-reserved';
      case 'issue':
        return 'tg-issue';
      default:
        return 'tg-default'; // Gray fallback
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircle className="tg-icon" />;
      case 'occupied-paid':
        return <Clock className="tg-icon" />;
      case 'occupied-eating':
        return <Users className="tg-icon" />;
      case 'reserved':
        return <Clock className="tg-icon" />;
      case 'issue':
        return <AlertTriangle className="tg-icon" />;
      default:
        return null;
    }
  };

  const getStatusText = (status, tableNumber) => {
    // Check if table has pending payment - show "Payment Pending"
    if (tableNumber && pendingPayments[tableNumber]) {
      return 'Payment Pending';
    }

    // ✅ FIX: Rely only on backend status for pay-first model
    // Don't override with confirmedPaymentTimestamps
    
    // Check if payment was confirmed and 2 minutes passed (for eat-first ash transition)
    if (tableNumber && confirmedPaymentTimestamps[tableNumber]) {
      const confirmData = confirmedPaymentTimestamps[tableNumber];
      const timeSinceConfirmation = Date.now() - confirmData.timestamp;
      if (timeSinceConfirmation >= 120000) {
        // More than 2 minutes - show Available (ash)
        return 'Available'; // ASH - waiting for next customer
      }
      // Less than 2 minutes - let backend status determine the text
    }

    switch (status) {
      case 'available':
        return 'Available';
      case 'occupied-paid':
        // Show "Paid Successfully" only when items are served (backend sets this status)
        return 'Paid Successfully';
      case 'occupied-eating':
        return 'Occupied';
      case 'reserved':
        return 'Reserved';
      case 'issue':
        return 'Issue';
      case 'clearing':
        return 'Cleared';
      default:
        return 'Unknown';
    }
  };

  // Helpers for Table Details modal
  const getTimeSpentMs = (table) => {
    // If session is completed (ash table), return 0 to show 00:00:00
    if (tableDetails?.sessionStatus === 'completed') {
      return 0;
    }

    // If table is paid, calculate time from start to payment time (frozen - don't use nowMs)
    if (table?.status === 'occupied-paid' && tableDetails?.paidAt) {
      const startTime = tableDetails.startedAt ? new Date(tableDetails.startedAt).getTime() : Date.now() - 15 * 60000;
      const endTime = new Date(tableDetails.paidAt).getTime();
      const frozenDuration = Math.max(0, endTime - startTime);
      return frozenDuration;
    }

    // For unpaid tables, calculate time from start to now (live timer)
    // ✅ FIX: Both timestamps are in UTC, just calculate difference directly
    // No need to convert to IST since we're calculating elapsed time (duration)
    if (tableDetails?.startedAt) {
      const startTime = new Date(tableDetails.startedAt).getTime();
      const liveDuration = Math.max(0, Date.now() - startTime);
      return liveDuration;
    }

    // Fallback: use lastActionTime or 15 minutes ago
    const base = table?.lastActionTime instanceof Date ? table.lastActionTime.getTime() : Date.now() - 15 * 60000;
    return Math.max(0, nowMs - base);
  };

  const splitHMS = (ms) => {
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s];
  };

  const hasCurrentOrder = (table) => {
    return table && table.billTotal && table.billTotal > 0 && table.status !== 'available';
  };

  const openOrdersForTable = async (table) => {
    // Auto-close the table details modal if it's open, so history view isn't overlapped
    if (detailModal) {
      setDetailModal(null);
    }

    // Abort any in-flight history request to avoid race conditions
    try { tableOrdersAbortRef.current?.abort(); } catch { }
    const controller = new AbortController();
    tableOrdersAbortRef.current = controller;

    // Immediately show the orders view in loading state for snappy UX
    setTableOrdersModal({ table, orders: null, loading: true, error: null });

    // Optional: add a timeout so we don't wait forever on slow networks
    const timeoutId = setTimeout(() => {
      try { controller.abort(); } catch { }
    }, 10000); // 10s

    try {
      const businessId = getBusinessId();
      const url = `${API_BASE}/waiter/table/${table.id}/orders?businessId=${businessId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        },
        signal: controller.signal
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error: ${response.status}`, errorText);
        throw new Error(`Failed to fetch orders: ${response.statusText}`);
      }

      const data = await response.json();

      // Map backend response to UI format
      const mappedOrders = (data.orders || []).map(order => {
        // Use shortened UUID (first 8 characters) if orderNumber is not available
        const displayOrderId = order.orderNumber || (order.orderId ? order.orderId.substring(0, 8).toUpperCase() : 'N/A');

        return {
          id: displayOrderId,
          orderId: order.orderId,
          paid: order.paymentStatus === 'paid',
          time: order.orderTime ? new Date(order.orderTime).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }) : '',
          total: order.totalAmount || 0,
          items: (order.items || []).map(item => ({
            name: item.name,
            qty: item.quantity,
            amount: item.price * item.quantity,
            price: item.price
          })),
          paymentMethod: order.paymentMethod,
          customerName: order.customerName
        };
      });

      setTableOrdersModal({ table, orders: mappedOrders, loading: false, error: null });
    } catch (err) {
      if (err?.name === 'AbortError') {
        console.warn('⚠️ Order history fetch aborted');
        // Keep modal visible with loading indicator until a new request starts or user closes
        setTableOrdersModal(prev => prev ? { ...prev, loading: true } : { table, orders: [], loading: true });
      } else {
        console.error('❌ Error fetching table orders:', err);
        // Fallback to empty orders but indicate error
        setTableOrdersModal(prev => ({ table: (prev?.table || table), orders: [], loading: false, error: String(err?.message || 'Failed to load orders') }));
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Fetch order history for waiter's assigned tables
  const openWaiterOrderHistory = async () => {
    if (!selectedWaiter || waiterAssignments.length === 0) {
      showToastNotification('Please select a waiter first', 'error');
      return;
    }

    const assignment = waiterAssignments.find(w => w.waiterName === selectedWaiter);
    if (!assignment) {
      showToastNotification('Waiter assignment not found', 'error');
      return;
    }

    
    // Show modal in loading state
    setWaiterOrderHistoryModal({ ordersByTable: null, loading: true, error: null });

    try {
      const businessId = getBusinessId();
      const url = `${API_BASE}/waiter/order-history?businessId=${businessId}&fromTable=${assignment.fromTable}&toTable=${assignment.toTable}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        }
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error: ${response.status}`, errorText);
        throw new Error(`Failed to fetch order history: ${response.statusText}`);
      }

      const data = await response.json();

      setWaiterOrderHistoryModal({
        ordersByTable: data.ordersByTable || [],
        loading: false,
        error: null,
        waiterName: selectedWaiter,
        fromTable: assignment.fromTable,
        toTable: assignment.toTable
      });

    } catch (err) {
      console.error('❌ Error fetching waiter order history:', err);
      setWaiterOrderHistoryModal({
        ordersByTable: [],
        loading: false,
        error: String(err?.message || 'Failed to load order history'),
        waiterName: selectedWaiter,
        fromTable: assignment.fromTable,
        toTable: assignment.toTable
      });
    }
  };

  // ===== END INTEGRATED TABLEGRID FUNCTIONALITY =====

  // ===== INLINE TABLEGRID COMPONENT =====
  const InlineTableGrid = ({ tables, onTableAction, showRevenue = true, callRequests = [] }) => {
    return (
      <>
        <div className="tg-grid">
          {tables.map(table => {
            const isCallingWaiter = hasActiveCall(table.id);
            const isClearing = clearingTables.has(table.id);

            return (
              <div
                key={table.id}
                className={`tg-card ${getTableColorClass(table.status, table.id)} ${isClearing ? 'tg-ash' : ''} ${fetchingTableId === table.id ? 'tg-fetching' : ''}`}
                onClick={async () => {
                  // Prevent multiple clicks while fetching
                  if (fetchingTableId) return;
                  // Fetch table details first before opening modal
                  const details = await fetchTableDetails(table.id);
                  // Only open modal after data is ready (prevents blinking)
                  if (details) {
                    setDetailModal({ table, hasActiveCall: isCallingWaiter, loading: false, tableDetails: details, hasPendingPayment: !!pendingPayments[table.id] });
                    onTableAction?.(table.id, 'select');
                  }
                }}
                style={{ cursor: fetchingTableId === table.id ? 'wait' : 'pointer', opacity: fetchingTableId === table.id ? 0.7 : 1 }}
              >
                {(() => {
                  // Check if table is in ASH state (confirmed payment > 2 minutes ago)
                  const isAshState = confirmedPaymentTimestamps[table.id] && 
                    (Date.now() - confirmedPaymentTimestamps[table.id].timestamp) >= 120000;
                  
                  // Empty/Available tables OR ASH state - centered text only
                  if (table.status === 'available' || isClearing || isAshState) {
                    return (
                      <div className="tg-empty-center">
                        Table {table.id}
                      </div>
                    );
                  }
                  
                  // Occupied tables - show full details
                  return (
                    <>
                      {/* Table Header with Name and Icon */}
                      <div className="tg-table-name">
                        Table {table.id}
                        {table.status === 'occupied-paid' && (
                          <div className="tg-table-icon tg-table-icon--paid" aria-label="Paid">
                            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#25C35E' }}>✓</span>
                          </div>
                        )}
                        {table.status === 'occupied-eating' && (
                          <div className="tg-table-icon">
                            <Clock size={18} />
                          </div>
                        )}
                      </div>

                      {/* Status Text */}
                      <div className="tg-table-status" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{isClearing ? 'Cleared' : getStatusText(table.status, table.id)}</span>
                        {isCallingWaiter && (
                          <Bell size={30} color="#000000" strokeWidth={2.5} className="tg-bell-icon" />
                        )}
                      </div>

                      {/* Bottom Row: Amount and Time */}
                      <div className="tg-table-bottom">
                        {showRevenue && table.billTotal !== undefined && table.billTotal !== null && (
                          <div className="tg-table-amount">₹{Number(table.billTotal).toFixed(0)}</div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                          {table.startedAt && (
                            <div className="tg-table-time">
                              {Math.floor((Date.now() - new Date(table.startedAt).getTime()) / 60000)}m
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            );
          })}
        </div>
        {/* New Table Details Modal (screenshot-accurate) */}
        {detailModal && ReactDOM.createPortal(
          <div key={`modal-${detailModal.table.id}`} className="wd-table-modal-backdrop" onClick={() => { setDetailModal(null); setTableDetails(null); }}>
            <div className={`wd-table-modal-container${hasCurrentOrder(detailModal.table) ? '' : ' wd-table-modal-container--empty'}`} onClick={e => e.stopPropagation()}>
              <div className="wd-table-modal-header">
                <h3 className="wd-table-modal-title">Table {detailModal.table.id} Details</h3>
                {detailModal.hasActiveCall && (
                  <button
                    className="t2-primary t2-accept-btn"
                    onClick={() => acknowledgeWaiterCall(detailModal.table.id)}
                  >
                    Accept Request
                  </button>
                )}
                <button className="wd-table-modal-close" onClick={() => { setDetailModal(null); setTableDetails(null); }}>×</button>
              </div>
              {detailModal.hasPendingPayment && (
                <div className="t2-payment-buttons" style={{ display: 'flex', gap: '12px', padding: '16px', borderBottom: '1px solid #374151', justifyContent: 'space-between' }}>
                  <button
                      className="payment-cancel-btn"
                      style={{ background: '#ef4444', color: 'white', flex: 1, height: '54px', fontSize: '16px', fontWeight: '600', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                      onClick={() => {
                        const tableNum = detailModal.table.id;
                        
                        // Stop voice announcement for THIS table
                        if (paymentVoiceIntervalsRef.current[tableNum]) {
                          clearInterval(paymentVoiceIntervalsRef.current[tableNum]);
                          delete paymentVoiceIntervalsRef.current[tableNum];
                        }
                        
                        // Remove from announced set
                        announcedPaymentsRef.current.delete(`payment-${tableNum}`);
                        
                        // Remove from pending payments
                        setPendingPayments(prev => {
                          const updated = { ...prev };
                          delete updated[tableNum];
                          return updated;
                        });
                        
                        // Close modal and refresh tables
                        setDetailModal(null);
                        setTableDetails(null);
                        fetchTables();
                        
                        showToastNotification(`❌ Payment cancelled for Table ${tableNum}`, 'error');
                      }}
                    >
                      Cancel
                    </button>
                  <button
                      className="payment-confirm-btn"
                      style={{ background: '#10b981', color: 'white', flex: 1, height: '54px', fontSize: '16px', fontWeight: '600', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                      onClick={async () => {
                        const tableNum = detailModal.table.id;
                        const payment = pendingPayments[tableNum];
                        
                        if (!payment) return;
                        
                        try {
                          // Send confirmation to backend to mark payment as completed
                          const businessId = getBusinessId();
                          const response = await fetch(`${API_BASE}/qr-billing/confirm-upi-payment`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${localStorage.getItem('token')}`,
                              'X-Business-ID': businessId
                            },
                            body: JSON.stringify({
                              businessId,
                              tableNumber: tableNum,
                              orderId: payment.orderId,
                              amount: payment.amount,
                              confirmedBy: localStorage.getItem('userName') || 'Waiter'
                            })
                          });
                          
                          if (!response.ok) {
                            throw new Error('Failed to confirm payment');
                          }
                          
                          const result = await response.json();
                          
                        } catch (error) {
                          console.error('❌ Error confirming payment with backend:', error);
                          // Continue with local update even if backend fails
                        }
                        
                        // Stop voice announcement for THIS table
                        if (paymentVoiceIntervalsRef.current[tableNum]) {
                          clearInterval(paymentVoiceIntervalsRef.current[tableNum]);
                          delete paymentVoiceIntervalsRef.current[tableNum];
                        }
                        
                        // Remove from announced set
                        announcedPaymentsRef.current.delete(`payment-${tableNum}`);
                        
                        // Set confirmed payment timestamp for 1-minute green period
                        const currentTable = tables.find(t => t.id === tableNum);
                        setConfirmedPaymentTimestamps(prev => ({
                          ...prev,
                          [tableNum]: {
                            timestamp: Date.now(),
                            sessionId: currentTable?.sessionId || null
                          }
                        }));
                        
                        // Remove from pending payments FIRST
                        setPendingPayments(prev => {
                          const updated = { ...prev };
                          delete updated[tableNum];
                          return updated;
                        });
                        
                        // Update table status locally to green (paid)
                        setTables(prevTables => {
                          const updatedTables = prevTables.map(t => 
                            t.id === tableNum ? { ...t, status: 'occupied-paid', has_unpaid_orders: false } : t
                          );
                          const updatedTable = updatedTables.find(t => t.id === tableNum);
                          return updatedTables;
                        });
                        
                        // Close modal and refresh tables
                        setDetailModal(null);
                        setTableDetails(null);
                        fetchTables();
                        
                        showToastNotification(`✅ Payment confirmed for Table ${tableNum}`, 'success');
                      }}
                    >
                      Confirm Payment
                    </button>
                  </div>
                )}
              <div className="wd-table-modal-body" ref={modalBodyRef}>
                <div className="wd-table-modal-content">

                  {hasCurrentOrder(detailModal.table) ? (
                    <>
                      {/* Current Orders */}
                      <div className="t2-section">
                        <div className="t2-title">Current Orders</div>
                        {/* Real-time order items from database */}
                        {(() => {
                          // Get the most recent order based on creation time
                          const orders = detailModal?.tableDetails?.orders || [];

                          // Sort orders by created time (most recent first)
                          const sortedOrders = [...orders].sort((a, b) => {
                            const timeA = new Date(a.createdAt || a.placedAt || a.created_at || 0).getTime();
                            const timeB = new Date(b.createdAt || b.placedAt || b.created_at || 0).getTime();
                            return timeB - timeA; // Descending order (most recent first)
                          });

                          // Find the latest unpaid order, or if all paid, show the most recent order
                          const latestUnpaidOrder = sortedOrders.find(o => o.paymentStatus !== 'paid');
                          const latestOrder = latestUnpaidOrder || sortedOrders[0];

                          if (!latestOrder || !latestOrder.items || latestOrder.items.length === 0) {
                            return (
                              <div className="t2-items">
                                <div className="t2-item" style={{ opacity: 0.5 }}>
                                  <div>No items ordered yet</div>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <>
                              <div className="t2-items">
                                {latestOrder.items.map((item, idx) => (
                                  <div key={idx} className="t2-item">
                                    <div>
                                      <div className="t2-item-name">{item.name} <span className="t2-item-qty">X {item.quantity}</span></div>
                                    </div>
                                    <div className="t2-item-price">₹{Number(item.price * item.quantity).toFixed(2)}</div>
                                  </div>
                                ))}
                              </div>

                              {/* Thin line before Total */}
                              <div className="t2-thin-line" />

                              {/* Order total row below items */}
                              <div className="t2-row between t2-total-row">
                                <div className="t2-label">Total</div>
                                <div className="t2-amount">₹{Number(latestOrder.totalAmount || 0).toFixed(2)}</div>
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* Time Spent - Using refs for direct DOM updates (no re-renders) */}
                      <div className="t2-section">
                        <div className="t2-title">Time Spent</div>
                        <div className="t2-time">
                          {(() => {
                            const [h, m, s] = splitHMS(getTimeSpentMs(detailModal.table));
                            const pad = (n) => String(n).padStart(2, '0');
                            return (
                              <>
                                <div className="t2-time-box"><div className="n" ref={timerHoursRef}>{pad(h)}</div><div className="l">Hours</div></div>
                                <div className="t2-time-box"><div className="n" ref={timerMinutesRef}>{pad(m)}</div><div className="l">Minutes</div></div>
                                <div className="t2-time-box"><div className="n" ref={timerSecondsRef}>{pad(s)}</div><div className="l">Seconds</div></div>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Payment QR Code - Show if uploaded by owner */}
                      {paymentQrImageUrl && (
                        <div className="t2-section" style={{ marginBottom: '10px', marginTop: '10px' }}>
                          <div className="t2-title">Payment QR Code</div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '16px',
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                          }}>
                            <img 
                              src={paymentQrImageUrl} 
                              alt="Payment QR Code" 
                              style={{
                                maxWidth: '250px',
                                maxHeight: '250px',
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain'
                              }}
                              onError={(e) => {
                                console.error('❌ Failed to load payment QR image');
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Payment Details - Moved to bottom */}
                      <div className="t2-section t2-payment" style={{ marginBottom: '8px', marginTop: '10px' }}>
                        <div className="t2-title">Payment Details</div>
                        <div className="t2-card-row">
                          <div className="t2-card-left">
                            <div className="t2-label">Payment Method</div>
                          </div>
                          <div className={`t2-card-right ${detailModal.table.status === 'occupied-paid' ? '' : 'not-paid'}`}>
                            {/* ✅ FIXED: Check if order is actually paid, not just table status */}
                            {detailModal?.tableDetails?.orders?.[0]?.paymentStatus === 'paid'
                              ? (detailModal.tableDetails.orders[0].paymentMethod
                                ? getPaymentMethodDisplay(detailModal.tableDetails.orders[0].paymentMethod)
                                : 'Cash')
                              : <span className="t2-status pending">Pending</span>}
                          </div>
                        </div>
                      </div>

                      <button className="t2-primary gold" style={{ marginBottom: '8px', marginLeft: '12px', marginRight: '12px' }} onClick={() => openOrdersForTable(detailModal.table)}>View all orders</button>
                      <button className="t2-primary" style={{ background: '#444', color: '#fff', marginLeft: '12px', marginRight: '12px' }} onClick={() => { setDetailModal(null); setTableDetails(null); onTableAction?.(detailModal.table.id, 'cleared'); }}>Clear Table/Reset</button>
                    </>
                  ) : (
                    // Empty table view
                    <div className="t2-empty">
                      <div className="t2-empty-icon" aria-hidden>🍽</div>
                      <div className="t2-empty-title">Table is Empty</div>
                      <div className="t2-empty-sub">No active session at this table</div>
                      <button className="t2-primary gold" onClick={() => openOrdersForTable(detailModal.table)}>
                        View Order History for This Table
                      </button>
                      <button className="t2-primary" style={{ background: '#444', color: '#fff' }} onClick={() => { setDetailModal(null); setTableDetails(null); onTableAction?.(detailModal.table.id, 'cleared'); }}>Clear Table/Reset</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>, document.body
        )}

        {/* Legacy small timer modal (kept for debugging if needed) */}
        {timerModal && (
          <div className="tg-modal-overlay" onClick={() => setTimerModal(null)}>
            <div className="tg-modal" onClick={e => e.stopPropagation()}>
              <div className="tg-modal-header">
                <h3>Table {timerModal.tableId} Timer</h3>
                <button className="tg-close" onClick={() => setTimerModal(null)}>×</button>
              </div>
              <div className="tg-modal-body">
                {timerModal.loading && <div className="tg-loading">Loading...</div>}
                {!timerModal.loading && timerModal.error && (
                  <div className="tg-error">{timerModal.error}</div>
                )}
                {!timerModal.loading && !timerModal.error && (
                  <>
                    <div className="tg-row">
                      <span>Status:</span>
                      <strong>{timerModal.state?.is_paused ? 'Stopped' : 'Running'}</strong>
                    </div>
                    {timerModal.state?.is_paused && timerModal.state?.paused_at && (
                      <div className="tg-row small">Stopped at: {new Date(timerModal.state.paused_at).toLocaleTimeString()}</div>
                    )}
                    <div className="tg-actions">
                      {!timerModal.state?.is_paused && (
                        <button className="tg-action-btn pause" onClick={actStop}>
                          <PauseCircle size={18} /> Stop Timer
                        </button>
                      )}
                      <button className="tg-action-btn clear" onClick={actClearTable} style={{ backgroundColor: '#ef4444', marginLeft: '8px' }}>
                        <Trash2 size={18} /> Clear Table
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };
  // ===== END INLINE TABLEGRID COMPONENT =====

  // Filter tables based on selected waiter's assigned table range
  const getWaiterTables = () => {
    if (!selectedWaiter || waiterAssignments.length === 0) {
      // If no waiter selected or no assignments, show all tables
      return tables;
    }

    const assignment = waiterAssignments.find(w => w.waiterName === selectedWaiter);
    if (!assignment) {
      return tables;
    }

    // Filter tables by assigned range
    return tables.filter(table => {
      // Safely extract table number with null checks
      let tableNum = 0;
      if (table.tableNumber) {
        tableNum = parseInt(table.tableNumber);
      } else if (table.table) {
        tableNum = parseInt(table.table);
      } else if (table.name) {
        tableNum = parseInt(table.name.replace(/[^0-9]/g, ''));
      } else if (table.id) {
        // Try to extract number from id as last resort
        const match = String(table.id).match(/\d+/);
        tableNum = match ? parseInt(match[0]) : 0;
      }

      // Only include if we got a valid number and it's in range
      if (isNaN(tableNum) || tableNum === 0) {
        console.warn('Could not extract table number from:', table);
        return false;
      }

      return tableNum >= assignment.fromTable && tableNum <= assignment.toTable;
    });
  };

  // Filter pickup queue items based on selected waiter's assigned table range
  const getWaiterPickupQueue = (queueItems) => {
    if (!selectedWaiter || waiterAssignments.length === 0) {
      // If no waiter selected or no assignments, show all items
      return queueItems;
    }

    const assignment = waiterAssignments.find(w => w.waiterName === selectedWaiter);
    if (!assignment) {
      return queueItems;
    }

    // Filter pickup queue items by assigned table range
    return queueItems.filter(item => {
      // Get table number from the item
      const tableNum = parseInt(item.tableNumber);

      // Only include if we got a valid number and it's in range
      if (isNaN(tableNum) || tableNum === 0) {
        console.warn('Could not extract table number from pickup queue item:', item);
        return false;
      }

      return tableNum >= assignment.fromTable && tableNum <= assignment.toTable;
    });
  };

  const myTables = getWaiterTables();
  const myPickupQueue = getWaiterPickupQueue(pickupQueue);
  const myOnDeliveryItems = getWaiterPickupQueue(onDeliveryItems);

  // Only show occupied tables (yellow=seated/unpaid, green=paid)
  // ✅ UPDATED: Also show available/ash tables (like Owner Dashboard)
  // Also show clearing tables for 5 seconds (ash state)
  const occupiedTables = myTables.filter(table =>
    table.status === 'occupied-paid' ||
    table.status === 'occupied-eating' ||
    table.status === 'issue' ||
    table.status === 'clearing' ||
    table.status === 'available' ||
    table.status === 'ash' ||
    clearingTables.has(table.id)
  );

  // Event handlers - simplified without backend calls
  const handleAcknowledgePickup = (ticketId) => {
    const item = pickupQueue.find(p => p.id === ticketId);
    if (!item) return;
    pickupTicket(ticketId);
    const itemCount = item.items ? item.items.length : 0;
    const task = {
      id: `delivery-${ticketId}`,
      type: 'pickup',
      description: `Deliver ${itemCount} item(s) to Table ${item.tableNumber}`,
      tableNumber: item.tableNumber,
      priority: 'high',
      createdAt: new Date()
    };
    setActiveTasks(prev => [...prev, task]);
  };

  const handleConfirmDelivered = (ticketId) => {
    const order = onDeliveryItems.find(p => p.id === ticketId) || pickupQueue.find(p => p.id === ticketId);
    if (!order) return;
    const servedItems = (order.items || []).filter(i => !i.id.includes('-served-')).map(it => ({ id: it.id, quantity: it.quantity }));
    handleDeliverSelectedItems(ticketId, servedItems);
  };

  // New: deliver a subset of items from an order in onDeliveryItems
  const handleDeliverSelectedItems = async (orderId, selectedItems = []) => {
    // selectedItems is now array of {id, quantity, portion_id} from modal
    // Keep the portion_id field when sending to backend
    const itemsForStore = Array.isArray(selectedItems[0]) ?
      selectedItems.map(id => ({ id, quantity: 1, portion_id: id })) :
      selectedItems.map(item => ({
        id: item.id,
        quantity: item.quantity || 1,
        portion_id: item.portion_id || item.id  // ✅ Preserve portion_id
      }));


    // Extract actual order ID (remove 'pickup-' prefix if present)
    const actualOrderId = orderId.replace('pickup-', '');

    // Make direct API call instead of using store
    const API_BASE = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    try {

      const response = await fetch(`${API_BASE}/waiter/deliver`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          orderId: actualOrderId,
          selectedItems: itemsForStore
        })
      });

      const data = await response.json();

      if (data.success) {
        // Show success feedback
        
        // ✅ IMMEDIATE STATE UPDATE: Remove or update the order in local state
        // This prevents the card from lingering while waiting for WebSocket update
        
        // Check if order is fully delivered
        if (data.orderCompleted) {
          // Remove from both pickup queue and onDelivery items
          setPickupQueue(prev => prev.filter(item => 
            item.id !== orderId && 
            !item.orderIds?.includes(parseInt(actualOrderId))
          ));
          setOnDeliveryItems(prev => prev.filter(item => 
            item.id !== orderId && 
            !item.orderIds?.includes(parseInt(actualOrderId))
          ));
        } else {
          // Update quantities in state immediately
          const updateOrderState = (orders) => {
            return orders.map(order => {
              if (order.id === orderId || order.orderIds?.includes(parseInt(actualOrderId))) {
                // Update item quantities by removing served items
                const updatedItems = order.items.map(item => {
                  const servedItem = itemsForStore.find(s => 
                    s.portion_id === item.portion_id || s.id === item.id
                  );
                  if (servedItem) {
                    const newQty = item.quantity - servedItem.quantity;
                    return { ...item, quantity: newQty > 0 ? newQty : 0, status: newQty > 0 ? item.status : 'served' };
                  }
                  return item;
                }).filter(item => item.quantity > 0); // Remove fully served items
                
                // If no items left, filter will remove this order in parent
                if (updatedItems.length === 0) return null;
                
                // Recalculate quantities
                const newReadyQty = updatedItems.reduce((sum, item) => 
                  (item.status === 'ready_on_pass' || item.status === 'pending' || !item.status) ? sum + item.quantity : sum, 0
                );
                const newServedQty = order.servedQuantity + itemsForStore.reduce((sum, s) => sum + s.quantity, 0);
                
                return {
                  ...order,
                  items: updatedItems,
                  readyQuantity: newReadyQty,
                  servedQuantity: newServedQty
                };
              }
              return order;
            }).filter(order => order !== null && (order.readyQuantity > 0 || order.items.some(i => i.status === 'in_hand')));
          };
          
          setPickupQueue(updateOrderState);
          setOnDeliveryItems(updateOrderState);
        }
      } else {
        console.error('❌ Deliver API returned error:', data.error);
      }
    } catch (err) {
      console.error('❌ Error calling deliver API:', err);
    }

    // Refresh pickup queue after delivery to get updated counts (backup sync)
    setTimeout(() => {
      fetchPickupQueue();
    }, 500);

    // If fully served, update table status locally
    const t = getTickets().find(o => o.id === orderId);
    if (!t || (t.items || []).filter(i => !i.id.includes('-served-')).every(i => i.status === 'served' || i.quantity === 0)) {
      const tableNum = (t && t.tableNumber) || (pickupQueue.find(o => o.id === orderId)?.tableNumber) || (onDeliveryItems.find(o => o.id === orderId)?.tableNumber);
      if (tableNum) {
        setTables(prev => prev.map(table => {
          if (table.id === tableNum && table.status === 'occupied-paid') {
            return { ...table, status: 'occupied-eating', lastAction: 'Order delivered', lastActionTime: new Date() };
          }
          return table;
        }));
        setActiveTasks(prev => prev.filter(tk => tk.id !== `delivery-${orderId}`));
      }
    }
  };

  // Handle confirming take away delivery
  const handleConfirmTakeAwayDelivery = async (ticket, allItems = null) => {
    try {
      const businessId = getBusinessId();
      const token = localStorage.getItem('token');


      // Get ticket identifier for tracking animation
      const ticketId = ticket.ticketNumber || ticket.ticket_number || ticket.id;

      // Start animation - add card to removing set
      setRemovingTakeAwayCards(prev => new Set(prev).add(ticketId));

      // Wait 500ms for animation to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Extract portion IDs from all items
      const portionIds = (allItems || ticket.items).map(item => {
        return item.portion_id || item.id;
      }).filter(Boolean);


      const response = await fetch(`${API_BASE}/waiter/confirm-takeaway-delivery?businessId=${businessId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Business-ID': businessId
        },
        body: JSON.stringify({
          orderId: ticket.orderId,
          portionIds: portionIds
        })
      });

      const data = await response.json();

      if (data.success) {
        // Remove from state after animation completes
        setTakeAwayQueue(prev => prev.filter(t =>
          (t.ticketNumber || t.ticket_number || t.id) !== ticketId
        ));

        // Clean up the removing set
        setRemovingTakeAwayCards(prev => {
          const newSet = new Set(prev);
          newSet.delete(ticketId);
          return newSet;
        });

        // Refresh from server to ensure sync
        setTimeout(() => {
          fetchTakeAwayQueue();
        }, 300);
      } else {
        // If failed, remove from removing set (cancel animation)
        setRemovingTakeAwayCards(prev => {
          const newSet = new Set(prev);
          newSet.delete(ticketId);
          return newSet;
        });
      }
    } catch (err) {
      console.error('❌ Error confirming take away delivery:', err);
      // Remove from removing set on error
      const ticketId = ticket.ticketNumber || ticket.ticket_number || ticket.id;
      setRemovingTakeAwayCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticketId);
        return newSet;
      });
    }
  };

  const handleAcceptRequest = (requestId) => {
    const request = serviceRequests.find(r => r.id === requestId);
    if (request) {
      setServiceRequests(prev => prev.map(r =>
        r.id === requestId
          ? { ...r, status: 'accepted', assignedWaiter: currentWaiter, acceptedAt: new Date() }
          : r
      ));

      const task = {
        id: `service-${requestId}`,
        type: 'service',
        description: `${request.reason || 'Service request'} - Table ${request.tableNumber}`,
        tableNumber: request.tableNumber,
        priority: request.priority,
        createdAt: new Date()
      };
      setActiveTasks(prev => [...prev, task]);
    }
  };

  const handleClearRequest = (requestId) => {
    const request = serviceRequests.find(r => r.id === requestId);
    if (request && request.requestedAt) {
      const responseTime = Math.floor((new Date().getTime() - request.requestedAt.getTime()) / 60000);

      setServiceRequests(prev => prev.map(r =>
        r.id === requestId
          ? { ...r, status: 'cleared', clearedAt: new Date(), responseTime }
          : r
      ).filter(r => r.id !== requestId));

      setActiveTasks(prev => prev.filter(t => t.id !== `service-${requestId}`));
    }
  };

  // Handle sending waiter reply to customer
  const handleSendWaiterReply = async (tableNumber, message) => {
    if (!message || !message.trim()) return;

    const businessId = getBusinessId();
    const waiterName = selectedWaiter || currentWaiter || localStorage.getItem('userName') || 'Waiter';


    try {
      const response = await fetch(`${API_BASE}/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          businessId,
          tableNumber: String(tableNumber),
          sessionId: null,
          senderType: 'waiter',
          senderName: waiterName,
          message: message.trim(),
          messageType: 'text'
        })
      });

      const data = await response.json();

      if (data.success) {
        // Clear the input field immediately for better UX
        setChatReplyText(prev => ({ ...prev, [tableNumber]: '' }));

        // DON'T add to local state - let WebSocket handle it to avoid duplicates
        // The backend will broadcast the message back via WebSocket with proper ID

      } else {
        console.error('❌ Failed to send reply:', data.error);
        showToastNotification('Failed to send message. Please try again.', 'error');
      }

    } catch (error) {
      console.error('❌ Error sending waiter reply:', error);
      showToastNotification('Failed to send message. Please try again.', 'error');
    }
  };

  const handleAcceptCall = (tableNumber) => {
    // TODO: Replace with API call - await acceptCallRequest(tableNumber)
    setCallRequests(prev => prev.filter(req => req.table_number !== tableNumber));
  };

  const handleClearTable = async (tableNumber) => {
    try {
      // Call backend API to clear table
      const result = await clearTable(tableNumber);

      // Mark table as clearing (ash state)
      setClearingTables(prev => {
        const s = new Set(prev);
        s.add(tableNumber);
        return s;
      });

      // Update table to show clearing status
      setTables(prev => prev.map(t =>
        t.id === tableNumber
          ? { ...t, status: 'clearing', lastAction: 'Table cleared', lastActionTime: new Date(), billTotal: undefined }
          : t
      ));

      // After 5 seconds, update to available and remove from clearing set
      setTimeout(() => {
        // Update table to available status
        setTables(prev => prev.map(t =>
          t.id === tableNumber
            ? { ...t, status: 'available' }
            : t
        ));

        // Remove from clearing set so it disappears from waiter dashboard
        setClearingTables(prev => {
          const s = new Set(prev);
          s.delete(tableNumber);
          return s;
        });
      }, 5000);

    } catch (error) {
      console.error('❌ Error clearing table:', error);
      showToastNotification('Failed to clear table. Please try again.', 'error');
    }
  };

  // Action modal handlers
  const handleFlagIssue = () => {
    if (issueDescription.trim()) {
      setTables(prev => prev.map(table =>
        table.id === selectedTable
          ? { ...table, status: 'issue', lastAction: `Issue: ${issueDescription}`, lastActionTime: new Date() }
          : table
      ));

      const task = {
        id: `issue-${Date.now()}`,
        type: 'issue',
        description: `Issue reported: ${issueDescription}`,
        tableNumber: selectedTable,
        priority: 'high',
        createdAt: new Date()
      };
      setActiveTasks(prev => [...prev, task]);
      setIssueDescription('');
      setShowIssueModal(false);
    }
  };

  const handleCheckStatus = () => {
    const table = tables.find(t => t.id === selectedTable);
    if (table && table.lastAction && table.lastActionTime) {
      const timeDiff = new Date().getTime() - table.lastActionTime.getTime();
      const minutes = Math.floor(timeDiff / 60000);
      setStatusInfo(`${table.lastAction} (${minutes}m ago)`);
    } else {
      setStatusInfo('No recent activity');
    }
    setShowStatusModal(true);
  };

  const handleProcessPayment = () => {
    setTables(prev => prev.map(table =>
      table.id === selectedTable
        ? { ...table, status: 'occupied-paid', lastAction: 'Cash payment processed', lastActionTime: new Date() }
        : table
    ));

    const task = {
      id: `payment-${Date.now()}`,
      type: 'payment',
      description: `Cash payment processed for Table ${selectedTable}`,
      tableNumber: selectedTable,
      priority: 'medium',
      createdAt: new Date()
    };
    setActiveTasks(prev => [...prev, task]);
    setShowPaymentModal(false);
  };

  const handleApplyDiscount = () => {
    if (discountReason.trim()) {
      const table = tables.find(t => t.id === selectedTable);
      if (table && table.billTotal) {
        const discountAmount = (table.billTotal * discountPercent) / 100;
        const newTotal = table.billTotal - discountAmount;

        setTables(prev => prev.map(t =>
          t.id === selectedTable
            ? { ...t, billTotal: newTotal, lastAction: `${discountPercent}% discount applied`, lastActionTime: new Date() }
            : t
        ));

        const task = {
          id: `discount-${Date.now()}`,
          type: 'discount',
          description: `${discountPercent}% discount applied to Table ${selectedTable}: ${discountReason}`,
          tableNumber: selectedTable,
          priority: 'medium',
          createdAt: new Date()
        };
        setActiveTasks(prev => [...prev, task]);
      }
      setDiscountReason('');
      setDiscountPercent(10);
      setShowDiscountModal(false);
    }
  };

  const handleAddItem = () => {
    if (newItem.name && newItem.price && newItem.quantity) {
      const table = tables.find(t => t.id === selectedTable);
      if (table) {
        const newTotal = (table.billTotal || 0) + (newItem.price * newItem.quantity);

        setTables(prev => prev.map(t =>
          t.id === selectedTable
            ? { ...t, billTotal: newTotal, lastAction: `Added ${newItem.name}`, lastActionTime: new Date() }
            : t
        ));
      }
      setNewItem({ name: '', quantity: 1, price: 0, category: 'Main' });
      setShowAddItemModal(false);
    }
  };

  const getScreenTitle = () => {
    switch (activeScreen) {
      case 'dashboard':
        return 'Waiter Dashboard';
      case 'pickup':
        return 'Pickup Queue';
      case 'takeaway':
        return '';
      case 'service':
        return 'Service Requests';
      case 'actions':
        return 'Quick Actions';
      default:
        return 'Pickup Queue';
    }
  };

  // Service requests helper functions
  const getRequestAge = (requestedAt) => {
    return Math.floor((new Date().getTime() - requestedAt.getTime()) / 60000);
  };

  const getPriorityIcon = (priority, escalated) => {
    if (escalated) return <AlertTriangle className="sr-icon sr-red" />;
    switch (priority) {
      case 'high':
        return <AlertTriangle className="sr-icon sr-red" />;
      case 'medium':
        return <Clock className="sr-icon sr-yellow" />;
      case 'low':
        return <Bell className="sr-icon sr-blue" />;
      default:
        return <Bell className="sr-icon sr-gray" />;
    }
  };

  const pendingRequests = serviceRequests.filter(r => r.status === 'pending');
  const acceptedRequests = serviceRequests.filter(r => r.status === 'accepted');

  // Modal component
  const Modal = ({ show, onClose, title, children }) => {
    if (!show) return null;
    return (
      <div className="modal-backdrop">
        <div className="modal">
          <div className="modal-header">
            <h3 className="modal-title">{title}</h3>
            <button onClick={onClose} className="modal-close"><X className="icon" /></button>
          </div>
          {children}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeScreen) {
      case 'dashboard':
        return (
          <div className="wd-space-y-6">
            {/* Stats Cards */}
            <div className="wd-grid-2">
              <div className="wd-card">
                <p className="wd-card-label">Active Tables</p>
                <Utensils className="wd-card-icon wd-icon-green" />
                <div className="wd-flex-between wd-card-stats-row">
                  <p className="wd-2xl">{occupiedTables.length}</p>
                </div>
              </div>

              <div className="wd-card">
                <p className="wd-card-label">Pending Tasks</p>
                <Clock className="wd-card-icon wd-icon-gold" />
                <div className="wd-flex-between wd-card-stats-row">
                  <p className="wd-2xl wd-gold-text">{callRequests.length}</p>
                </div>
              </div>
            </div>
            
            <div className="wd-card wd-card-tight">
              <h3 className="wd-h3">
                Occupied Tables ({occupiedTables.length})
              </h3>
              <InlineTableGrid
                tables={occupiedTables}
                onTableAction={(tableId, action) => {
                  if (action === 'cleared') {
                    handleClearTable(tableId);
                  } else if (action === 'accept-call') {
                    handleAcceptCall(tableId);
                  } else if (action === 'clear-request') {
                    // TODO: Replace with API call - await clearCallRequest(tableId)
                    setCallRequests(prev => prev.filter(req => req.table_number !== tableId));
                  }
                }}
                showRevenue={true}
                callRequests={callRequests}
              />
            </div>

            {activeTasks.length > 0 && (
              <div className="wd-card wd-card-top">
                <h3 className="wd-h3">
                  Active Tasks ({activeTasks.length})
                </h3>
                <div className="wd-vertical">
                  {activeTasks.map(task => (
                    <div key={task.id} className="wd-task">
                      <div className="wd-flex-between">
                        <span className="wd-task-desc">{task.description}</span>
                        <span className={`wd-task-priority ${task.priority}`}>{task.priority}</span>
                      </div>
                      <div className="wd-task-meta">
                        {task.tableNumber && `Table ${task.tableNumber} • `}
                        {Math.floor((new Date().getTime() - task.createdAt.getTime()) / 60000)}m ago
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'pickup':
        return (
          <PickupQueue
            items={myPickupQueue}
            onDeliveryItems={myOnDeliveryItems}
            onAcknowledgePickup={handleAcknowledgePickup}
            onConfirmDelivered={handleConfirmDelivered}
            onDeliverSelected={handleDeliverSelectedItems}
            businessId={getBusinessId()}
            waiterAssignments={waiterAssignments}
            selectedWaiter={selectedWaiter}
            waiterDashboardEnabled={waiterDashboardEnabled}
          />
        );

      case 'takeaway':
        return (
          <div className="wd-screen">
            <div className="wd-section">
              {/* Take Away Orders Header - matching Pickup Queue style */}
              <div className="wpov-pq-card">
                <h2 className="wpov-pq-title">
                  <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'nowrap'}}>
                    <ShoppingBag size={20} color="#ffffff" />
                    <span style={{whiteSpace:'nowrap'}}>Take Away Orders ({takeAwayQueue.length})</span>
                  </div>
                </h2>
              </div>

              {takeAwayQueue.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 'calc(100vh - 300px)',
                  textAlign: 'center',
                  color: '#9ca3af'
                }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: '#f3f4f6' }}>
                    No Take Away Orders
                  </h3>
                  <p style={{ fontSize: '14px' }}>
                    Take away orders will appear here when ready
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {takeAwayQueue.map((ticket, index) => {
                    // Get the ordered time and adjust for timezone if needed
                    let orderedTime = ticket.orderedAt ? ticket.orderedAt.getTime() : Date.now();

                    // Calculate elapsed minutes from order time to now
                    const elapsedMinutes = Math.floor((nowMs - orderedTime) / 60000);

                    // Format time similar to pickup cards (e.g., "22h 11m")
                    const hours = Math.floor(elapsedMinutes / 60);
                    const minutes = elapsedMinutes % 60;
                    const timeDisplay = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                    const isUrgent = elapsedMinutes >= 20;

                    // Check if this card is being removed
                    const ticketId = ticket.ticketNumber || ticket.ticket_number || ticket.id;
                    const isRemoving = removingTakeAwayCards.has(ticketId);

                    return (
                      <div
                        key={ticket.id}
                        className={isRemoving ? 'card-removing' : ''}
                        style={{
                          backgroundColor: '#000000',
                          borderRadius: '12px',
                          padding: '8px',
                          border: '1px solid #374151',
                          borderLeft: '4px solid #25C35E',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {/* Ticket Header */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px'
                          }}>
                            {/* Items count on the left */}
                            <div style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#9ca3af',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Items ({ticket.items.length})
                            </div>
                            {/* Match pickup card timer style with clock icon */}
                            <div className="wpov-pq-elapsed">
                              <Clock className="wpov-pq-small" />
                              <span className={isUrgent ? 'wpov-pq-urgent-text' : ''}>{timeDisplay}</span>
                            </div>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            {/* Ticket number below items */}
                            <p className="wpov-pq-item-table" style={{ marginBottom: '0' }}>#{ticket.ticketNumber}</p>
                          </div>
                        </div>

                        {/* Items List */}
                        <div style={{ marginBottom: '16px' }}>
                          {ticket.items.map((item, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '8px',
                                backgroundColor: '#0B0B0B',
                                borderRadius: '8px',
                                marginBottom: '8px',
                                border: '1px solid #2f3237'
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '4px'
                                  }}>
                                    <div style={{
                                      fontSize: '16px',
                                      fontWeight: '500',
                                      color: '#f3f4f6'
                                    }}>
                                      {item.menu_item_name || item.name}
                                    </div>
                                    {item.isNewItem && (
                                      <span style={{
                                        background: '#fbbf24',
                                        color: '#000',
                                        padding: '3px 10px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        boxShadow: '0 2px 4px rgba(251, 191, 36, 0.3)'
                                      }}>
                                        NEW
                                      </span>
                                    )}
                                  </div>
                                  {/* Display customizations if present */}
                                  {item.customizations && Array.isArray(item.customizations) && item.customizations.length > 0 && (
                                    <div style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: '6px',
                                      marginTop: '6px'
                                    }}>
                                      {item.customizations.map((customization, custIdx) => (
                                        <span key={custIdx} style={{
                                          background: '#2d2d2d',
                                          color: '#fbbf24',
                                          padding: '3px 8px',
                                          borderRadius: '6px',
                                          fontSize: '11px',
                                          fontWeight: '500',
                                          border: '1px solid #3f3f3f'
                                        }}>
                                          {customization}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div style={{
                                  fontSize: '15px',
                                  fontWeight: '700',
                                  color: 'white'
                                }}>
                                  × {item.quantity}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Confirm Delivery Button */}
                        <button
                          onClick={() => {
                            // Directly deliver all items without modal
                            handleConfirmTakeAwayDelivery(ticket, ticket.items);
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#25C35E',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#20a850'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#25C35E'}
                        >
                          Confirm Delivery
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`wd-root ${activeScreen === 'dashboard' ? 'dashboard-active' : ''}`}>
      <div className="wd-header">
        <div className="wd-header-inner">
          <div className="wd-brand">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div className="wd-brand-title">INVEXIS</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Payment QR Icon - Show if QR uploaded */}
                {paymentQrImageUrl && (
                  <button
                    onClick={() => setShowQrModal(true)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      display: 'flex',
                      marginBottom: '0.8rem',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fbbf24',
                      fontSize: '18px',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    aria-label="View Payment QR Code"
                    title="View Payment QR Code"
                  >
                    <QrCode size={20} />
                  </button>
                )}
                {/* Home Icon - Navigate to Dashboard - Right side of title */}
                <button
                  onClick={() => {
                    // Store that we came from waiter dashboard for bidirectional navigation
                    localStorage.setItem('previousDashboard', 'waiter-dashboard');
                    if (goTo) {
                      goTo('dashboard');
                    }
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    display: 'flex',
                    marginBottom: '0.8rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    fontSize: '18px',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  aria-label="Go to Dashboard"
                  title="Go to Dashboard"
                >
                  <Home size={20} />
                </button>
              </div>
            </div>
            <div className="wd-brand-divider" />
            <div className="wd-brand-sub" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="wd-screen-title">{getScreenTitle()}</span>
                  {/* Order History button for Tables screen */}
                  {activeScreen === 'tables' && selectedWaiter && (
                    <div className="wd-header-actions">
                      <button
                        className="wpov-history-toggle"
                        onClick={openWaiterOrderHistory}
                      >
                        Order History
                      </button>
                    </div>
                  )}
                  {/* Takeaway Queue Title */}
                  {activeScreen === 'takeaway' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '-9px' }}>
                      <span style={{ fontSize: '1.125rem', fontWeight: '700', color: '#f3f4f6' }}>
                        Takeaway Queue
                      </span>
                    </div>
                  )}
                </div>
                {/* Order History button for Dashboard screen - replaces waiter dropdown */}
                {activeScreen === 'dashboard' && (
                <div className="wd-header-actions">
                  <button
                    className="wpov-history-toggle"
                    onClick={() => {
                      openWaiterOrderHistory();
                    }}
                    style={{
                      marginLeft: 'auto'
                    }}
                  >
                    Order History
                  </button>
                </div>
              )}
              {/* Waiter Selection Dropdown - hidden on dashboard screen */}
              {activeScreen !== 'dashboard' && waiterAssignments.length > 0 && (
                <select
                  className="wd-waiter-select"
                  value={selectedWaiter}
                  onChange={(e) => setSelectedWaiter(e.target.value)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#1f2937',
                    color: '#f3f4f6',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    outline: 'none',
                    marginLeft: 'auto'
                  }}
                >
                  <option value="">Select Waiter</option>
                  {waiterAssignments.map((waiter, idx) => (
                    <option key={idx} value={waiter.waiterName}>
                      {waiter.waiterName} (Tables {waiter.fromTable}-{waiter.toTable})
                    </option>
                  ))}
                </select>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Stop Bell button in top right corner */}
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '8px',
          fontSize: '13px',
          color: '#9ca3af'
        }}>
          {callRequests.length > 0 && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: 'black',
              color: '#ffffff',
              border: '1px solid #374151',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Volume2 size={18} color="#fff" />
              {callRequests.length} table{callRequests.length > 1 ? 's' : ''} calling
            </div>
          )}
        </div>
      </div>

      <div className="wd-content">
        {renderContent()}
      </div>

      {/* Service Requests Modal */}
      {showServiceModal && (
        <div className="sr-modal-backdrop" onClick={() => setShowServiceModal(false)}>
          <div className="sr-modal sr-large-modal" onClick={e => e.stopPropagation()}>
            <div className="sr-modal-header">
              <h3 className="sr-modal-title">Service Requests</h3>
              <button onClick={() => setShowServiceModal(false)} className="sr-modal-close">×</button>
            </div>
            <div className="sr-modal-body">
              {/* Only show Pending Service Requests if there are any */}
              {pendingRequests.length > 0 && (
                <div className="sr-card">
                  <h4 className="sr-title"><Bell className="sr-icon sr-red" />Pending Service Requests ({pendingRequests.length})</h4>
                  <div className="sr-list">
                    {pendingRequests.sort((a, b) => { if (a.escalated && !b.escalated) return -1; if (!a.escalated && b.escalated) return 1; return a.requestedAt.getTime() - b.requestedAt.getTime(); }).map(request => {
                      const age = getRequestAge(request.requestedAt);
                      const isUrgent = age > 2 || request.escalated;
                      return (
                        <div key={request.id} className={`sr-item ${request.escalated ? 'sr-escalated' : ''}`}>
                          <div className="sr-item-top">
                            <div>
                              <div className="sr-item-header">
                                {getPriorityIcon(request.priority, request.escalated)}
                                <span className="sr-table">Table {request.tableNumber}</span>
                                {request.escalated && <span className="sr-escalated-badge">ESCALATED</span>}
                              </div>
                              {request.reason && <p className="sr-reason">{request.reason}</p>}
                            </div>
                            <div className="sr-meta">
                              <div className={isUrgent ? 'sr-urgent' : 'sr-age'}>{age}m ago</div>
                              <div className="sr-priority-label">{request.priority}</div>
                            </div>
                          </div>
                          <button onClick={() => handleAcceptRequest(request.id)} className={`sr-accept ${request.escalated ? 'sr-accept-danger' : 'sr-accept-gold'}`}>
                            <User className="sr-icon-small" />Accept Request
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {acceptedRequests.length > 0 && (
                <div className="sr-card">
                  <h4 className="sr-title"><User className="sr-icon sr-green" />My Active Requests ({acceptedRequests.length})</h4>
                  <div className="sr-list">
                    {acceptedRequests.map(request => {
                      const acceptedAge = request.acceptedAt ? Math.floor((new Date().getTime() - request.acceptedAt.getTime()) / 60000) : 0;
                      return (
                        <div key={request.id} className="sr-active-item">
                          <div className="sr-item-top">
                            <div>
                              <div className="sr-active-header"><CheckCircle className="sr-icon sr-green" /><span className="sr-table">Table {request.tableNumber}</span></div>
                              {request.reason && <p className="sr-reason">{request.reason}</p>}
                            </div>
                            <div className="sr-meta">Working {acceptedAge}m</div>
                          </div>
                          <button onClick={() => handleClearRequest(request.id)} className="sr-clear"><CheckCircle className="sr-icon-small" />Clear Request</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Chat Messages Section */}
              {chatMessages.length > 0 && (() => {
                // Filter chat messages by assigned waiter's table range
                const assignment = waiterAssignments.find(w => w.waiterName === selectedWaiter);
                const filteredChats = chatMessages.filter(chat => {
                  if (!assignment) return true; // Show all if no assignment
                  const tableNum = parseInt(chat.table_number);
                  return tableNum >= assignment.fromTable && tableNum <= assignment.toTable;
                });

                if (filteredChats.length === 0) return null;

                // Count unread for this waiter
                const waiterUnreadCount = filteredChats.reduce((sum, item) => sum + parseInt(item.unread_count || 0), 0);

                return (
                  <div className="sr-card">
                    <h4 className="sr-title">💬 Customer Messages ({waiterUnreadCount} unread)</h4>
                    <div className="sr-list">
                      {filteredChats.map((chat, index) => {
                        const timeAgo = Math.floor((new Date() - new Date(chat.latest_message_time)) / 60000);
                        const isExpanded = expandedChatTable === chat.table_number;
                        const messages = tableMessages[chat.table_number] || [];

                        return (
                          <div key={`${chat.table_number}-${messages.length}`} className="chat-message-item">
                            <div className="chat-message-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <span className="sr-table" style={{ fontSize: '16px', fontWeight: '600' }}>Table {chat.table_number}</span>
                              <span className="chat-unread-badge" style={{ background: '#ef4444', color: 'white', padding: '4px 8px', borderRadius: '12px', fontSize: '12px' }}>{chat.unread_count} new</span>
                            </div>
                            <p className="chat-message-preview" style={{ color: '#888', fontSize: '13px', marginBottom: '12px' }}>
                              {timeAgo < 1 ? 'Just now' : `${timeAgo}m ago`}
                            </p>

                            {isExpanded && messages.length > 0 && (
                              <div
                                id={`chat-messages-${chat.table_number}`}
                                style={{
                                  background: '#1a1a1a',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  marginBottom: '12px',
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                  scrollBehavior: 'smooth'
                                }}
                              >
                                {messages.map((msg, idx) => {
                                  const role = msg.sender_type === 'customer' ? 'customer' : 'waiter';
                                  const isCompact = idx > 0 && messages[idx - 1]?.sender_type === msg.sender_type;
                                  return (
                                    <div
                                      key={msg.id || `${msg.sender_type}-${msg.message}-${msg.created_at}`}
                                      className={`chat-row ${role} ${isCompact ? 'compact' : ''}`}
                                    >
                                      <div className={`chat-bubble ${role}`}>
                                        <p className="chat-bubble-text">{msg.message}</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {isExpanded && messages.length === 0 && (
                              <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '20px', marginBottom: '12px', textAlign: 'center', color: '#666' }}>
                                No messages found
                              </div>
                            )}

                            {/* Reply Input - Only show when expanded */}
                            {isExpanded && (
                              <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <input
                                    type="text"
                                    placeholder="Type your reply..."
                                    value={chatReplyText[chat.table_number] || ''}
                                    onChange={(e) => setChatReplyText(prev => ({ ...prev, [chat.table_number]: e.target.value }))}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter' && chatReplyText[chat.table_number]?.trim()) {
                                        handleSendWaiterReply(chat.table_number, chatReplyText[chat.table_number]);
                                      }
                                    }}
                                    style={{
                                      flex: 1,
                                      background: '#0f0f0f',
                                      border: '1px solid #333',
                                      borderRadius: '6px',
                                      padding: '10px 12px',
                                      color: '#fff',
                                      fontSize: '14px',
                                      outline: 'none'
                                    }}
                                  />
                                  <button
                                    onClick={() => handleSendWaiterReply(chat.table_number, chatReplyText[chat.table_number])}
                                    disabled={!chatReplyText[chat.table_number]?.trim()}
                                    style={{
                                      background: chatReplyText[chat.table_number]?.trim() ? '#10b981' : '#333',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '10px 20px',
                                      fontWeight: '600',
                                      cursor: chatReplyText[chat.table_number]?.trim() ? 'pointer' : 'not-allowed',
                                      fontSize: '14px'
                                    }}
                                  >
                                    Send
                                  </button>
                                </div>
                              </div>
                            )}

                            <button
                              onClick={async () => {
                                if (isExpanded) {
                                  // Collapse
                                  setExpandedChatTable(null);
                                } else {
                                  // Expand and fetch messages
                                  const businessId = getBusinessId();

                                  try {
                                    const response = await fetch(`${API_BASE}/chat/messages/${businessId}/${chat.table_number}`);
                                    const data = await response.json();

                                    if (data.success) {
                                      const fetchedMessages = data.messages || [];

                                      setTableMessages(prev => ({
                                        ...prev,
                                        [chat.table_number]: fetchedMessages
                                      }));
                                      setExpandedChatTable(chat.table_number);
                                    } else {
                                      console.error('❌ Failed to fetch messages:', data.error);
                                    }
                                  } catch (error) {
                                    console.error('❌ Error fetching messages:', error);
                                  }
                                }
                              }}
                              className="sr-accept sr-accept-gold"
                              style={{ width: '100%', marginTop: '8px' }}
                            >
                              {isExpanded ? '▲ Hide Messages' : '▼ View Messages'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions Modal */}
      {showActionsModal && (
        <div className="modal-backdrop" onClick={() => setShowActionsModal(false)}>
          <div className="modal large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Quick Actions</h3>
              <button onClick={() => setShowActionsModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="ab-section">
                <label className="ab-label">Select Table</label>
                <select value={selectedTable} onChange={(e) => setSelectedTable(Number(e.target.value))} className="ab-select">
                  {tables.map(table => (
                    <option key={table.id} value={table.id}>Table {table.id} - {table.status}</option>
                  ))}
                </select>

                <div className="ab-actions">
                  <button onClick={() => setShowIssueModal(true)} className="ab-btn ab-btn-danger"><Flag className="ab-icon" />Flag for Issue</button>
                  <button onClick={() => setShowClearModal(true)} className="ab-btn ab-btn-green" disabled={tables.find(t => t.id === selectedTable)?.status === 'available'}><Trash2 className="ab-icon" />Clear Table/Reset</button>
                  <button onClick={handleCheckStatus} className="ab-btn ab-btn-gold"><Search className="ab-icon" />Check Table Status</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Sub-Modals */}
      <Modal show={showIssueModal} onClose={() => setShowIssueModal(false)} title={`Flag Issue - Table ${selectedTable}`}>
        <div className="modal-body">
          <label className="ab-label">Issue Description</label>
          <textarea value={issueDescription} onChange={(e) => setIssueDescription(e.target.value)} className="ab-textarea" placeholder="Describe the issue..." />
          <div className="modal-actions">
            <button onClick={handleFlagIssue} disabled={!issueDescription.trim()} className="ab-btn ab-btn-danger">Report Issue</button>
            <button onClick={() => setShowIssueModal(false)} className="ab-btn ab-btn-gray">Cancel</button>
          </div>
        </div>
      </Modal>

      {/* Full-screen Orders view (matches reference screenshot) */}
      {tableOrdersModal && (
        <div className="toh-overlay" onClick={() => setTableOrdersModal(null)}>
          <div className="toh-page" onClick={e => e.stopPropagation()}>
            <div className="toh-header">
              <button className="toh-back" onClick={() => setTableOrdersModal(null)} aria-label="Back">
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <div className="toh-title">Order History</div>
            </div>
            <div className="toh-body">
              {/* Loading or error states */}
              {tableOrdersModal.loading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '100%'
                }}>
                  <div style={{ fontSize: '42px', marginBottom: '16px' }}>⏳</div>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>Loading order history…</div>
                </div>
              )}
              {!tableOrdersModal.loading && tableOrdersModal.error && (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#d9534f' }}>
                  <div style={{ fontSize: '44px', marginBottom: '16px' }}>⚠️</div>
                  <div style={{ fontSize: '17px', fontWeight: 600, marginBottom: '8px' }}>Failed to Load Orders</div>
                  <div style={{ fontSize: '14px', marginBottom: '16px' }}>{tableOrdersModal.error}</div>
                  <button className="toh-retry" onClick={() => openOrdersForTable(tableOrdersModal.table)}>Retry</button>
                </div>
              )}
              {!tableOrdersModal.loading && !tableOrdersModal.error && tableOrdersModal.orders && (
                <>
                  <div className="toh-stats">
                    <div className="toh-stat">
                      <div className="n">{tableOrdersModal.orders.length}</div>
                      <div className="l">Orders</div>
                    </div>
                    <div className="toh-stat">
                      <div className="n">{tableOrdersModal.orders.reduce((sum, o) => sum + (o.items || []).reduce((s, i) => s + (i.qty || 0), 0), 0)}</div>
                      <div className="l">Sold</div>
                    </div>
                    <div className="toh-stat">
                      <div className="n">₹{tableOrdersModal.orders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(0)}</div>
                      <div className="l">Revenue</div>
                    </div>
                  </div>
                  <div className="toh-list">
                    {tableOrdersModal.orders.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                        <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>No Orders Yet</div>
                        <div style={{ fontSize: '14px' }}>This table has no orders today.</div>
                      </div>
                    ) : (
                      tableOrdersModal.orders.map((o, i) => (
                        <div key={i} className="toh-card">
                          <div className="toh-card-top">
                            <div className="toh-items-title">Items ({(o.items || []).reduce((a, b) => a + Number(b.qty || 0), 0)})</div>
                            <div className="toh-ord-id">{o.time || 'N/A'}</div>
                            <div className={"toh-badge " + (o.paid ? 'paid' : 'unpaid')}>{o.paid ? 'PAID' : 'UNPAID'}</div>
                          </div>
                          <div className="toh-items">
                            <div className="toh-items-box">
                              {(o.items || []).map((it, idx) => (
                                <div key={idx} className="toh-item-row">
                                  <div className="toh-item-name">{it.name} x{it.qty}</div>
                                  <div className="toh-item-amt">₹{Number(it.amount ?? it.price ?? 0).toFixed(0)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="toh-card-bottom">
                            <div className="toh-time">Total</div>
                            <div className="toh-total">₹{Number(o.total || 0).toFixed(0)}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Waiter Order History Modal */}
      {waiterOrderHistoryModal && (
        <div className="toh-overlay" onClick={() => setWaiterOrderHistoryModal(null)}>
          <div className="toh-page" onClick={e => e.stopPropagation()}>
            <div className="toh-header">
              <button className="toh-back" onClick={() => setWaiterOrderHistoryModal(null)} aria-label="Back">
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <div className="toh-title">
                Order History
              </div>
            </div>
            <div className="toh-body">
              {/* Loading or error states */}
              {waiterOrderHistoryModal.loading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '24px',
                  backgroundColor: '#1f2937',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <Clock size={32} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
                  <div style={{ fontSize: '16px', fontWeight: 500, color: '#f3f4f6' }}>Loading order history…</div>
                </div>
              )}

              {waiterOrderHistoryModal.error && !waiterOrderHistoryModal.loading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '24px',
                  backgroundColor: '#1f2937',
                  borderRadius: '12px',
                  textAlign: 'center',
                  maxWidth: '90%'
                }}>
                  <AlertTriangle size={32} style={{ color: '#ef4444' }} />
                  <div style={{ fontSize: '16px', fontWeight: 500, color: '#f3f4f6' }}>Error loading order history</div>
                  <div style={{ fontSize: '14px', color: '#9ca3af' }}>{waiterOrderHistoryModal.error}</div>
                  <button
                    onClick={() => setWaiterOrderHistoryModal(null)}
                    style={{
                      marginTop: '12px',
                      padding: '8px 16px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  >
                    Close
                  </button>
                </div>
              )}

              {/* Display orders grouped by table */}
              {!waiterOrderHistoryModal.loading && !waiterOrderHistoryModal.error && waiterOrderHistoryModal.ordersByTable && (
                <div className="oh-orders-list">
                  {waiterOrderHistoryModal.ordersByTable.length === 0 ? (
                    <div className="oh-empty">
                      <div className="oh-empty-text">No orders found for today</div>
                    </div>
                  ) : (
                    waiterOrderHistoryModal.ordersByTable.flatMap((tableData) => 
                      tableData.orders.map((order, orderIdx) => {
                        const orderTime = order.orderTime || order.placedAt || order.placed_at || order.createdAt;
                        const paymentStatus = order.paymentStatus || order.payment_status || 'pending';
                        const paymentMethod = order.paymentMethod || order.payment_method;
                        const totalAmount = order.totalAmount || order.total_amount || order.total || 0;
                        const orderNumber = order.orderNumber || order.order_number || order.order_code || order.orderId || order.order_id;

                        // Determine badge text based on payment status
                        let badgeText = 'UNPAID';
                        let badgeClass = 'unpaid';
                        if (paymentStatus === 'paid') {
                          badgeText = paymentMethod ? paymentMethod.toUpperCase() : 'PAID';
                          badgeClass = paymentMethod ? paymentMethod.toLowerCase() : 'paid';
                        }

                        return (
                          <div key={`${tableData.tableNumber}-${orderIdx}`} className="oh-order-card">
                            <div className="oh-order-header">
                              <div className="oh-order-id">Order {orderNumber}</div>
                              <div className={`oh-payment-badge ${badgeClass}`}>
                                {badgeText}
                              </div>
                            </div>
                            <div className="oh-order-table">Table {tableData.tableNumber}</div>

                            <div className="oh-order-items">
                              <div className="oh-items-label">Items ({order.items?.length || 0})</div>
                              {order.items?.map((item, i) => (
                                <div key={i} className="oh-item">
                                  {item.name || item.item_name} x{item.quantity} - ₹{(item.price * item.quantity).toFixed(0)}
                                </div>
                              ))}
                            </div>

                            <div className="oh-order-footer">
                              <div className="oh-order-time">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <polyline points="12 6 12 12 16 14" />
                                </svg>
                                {orderTime ? new Date(orderTime).toLocaleString('en-IN', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'N/A'}
                              </div>
                              <div className="oh-order-total">₹{totalAmount}</div>
                            </div>
                          </div>
                        );
                      })
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal show={showStatusModal} onClose={() => setShowStatusModal(false)} title={`Table ${selectedTable} Status`}>
        <div className="modal-body">
          <div className="ab-status-box">{statusInfo}</div>
          <button onClick={() => setShowStatusModal(false)} className="ab-btn ab-btn-gold">Close</button>
        </div>
      </Modal>

      <Modal show={showClearModal} onClose={() => setShowClearModal(false)} title={`Clear Table ${selectedTable}`}>
        <div className="modal-body">
          <p>Are you sure you want to clear and reset Table {selectedTable}? This will mark the table as available.</p>
          <div className="modal-actions">
            <button onClick={() => { handleClearTable(selectedTable); setShowClearModal(false); }} className="ab-btn ab-btn-green">Confirm Clear</button>
            <button onClick={() => setShowClearModal(false)} className="ab-btn ab-btn-gray">Cancel</button>
          </div>
        </div>
      </Modal>

      <Modal show={showPaymentModal} onClose={() => setShowPaymentModal(false)} title={`Process Cash Payment - Table ${selectedTable}`}>
        <div className="modal-body">
          <div className="ab-status-box">Bill Total: ₹{tables.find(t => t.id === selectedTable)?.billTotal?.toFixed(2) || '0.00'}</div>
          <p className="ab-note">This will mark the table as paid and log the cash transaction.</p>
          <div className="modal-actions">
            <button onClick={handleProcessPayment} className="ab-btn ab-btn-green">Process Payment</button>
            <button onClick={() => setShowPaymentModal(false)} className="ab-btn ab-btn-gray">Cancel</button>
          </div>
        </div>
      </Modal>

      <Modal show={showDiscountModal} onClose={() => setShowDiscountModal(false)} title={`Apply Discount - Table ${selectedTable}`}>
        <div className="modal-body">
          <div>
            <label className="ab-label">Discount Percentage</label>
            <input type="number" value={discountPercent} onChange={(e) => setDiscountPercent(Number(e.target.value))} max="100" className="ab-input" />
          </div>
          <div>
            <label className="ab-label">Reason for Discount</label>
            <textarea value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} className="ab-textarea" placeholder="Reason for discount/comp..." />
          </div>
          <div className="ab-discount-summary">
            <div>Original: ₹{tables.find(t => t.id === selectedTable)?.billTotal?.toFixed(2) || '0.00'}</div>
            <div>Discount: -{discountPercent}%</div>
            <span className="ab-new-total">New Total: ₹{((tables.find(t => t.id === selectedTable)?.billTotal || 0) * (1 - discountPercent / 100)).toFixed(2)}</span>
          </div>
          <div className="modal-actions">
            <button onClick={handleApplyDiscount} disabled={!discountReason.trim()} className="ab-btn ab-btn-blue">Apply Discount</button>
            <button onClick={() => setShowDiscountModal(false)} className="ab-btn ab-btn-gray">Cancel</button>
          </div>
        </div>
      </Modal>

      <Modal show={showAddItemModal} onClose={() => setShowAddItemModal(false)} title={`Add Item - Table ${selectedTable}`}>
        <div className="modal-body">
          <label className="ab-label">Item Name</label>
          <input type="text" value={newItem.name} onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))} className="ab-input" placeholder="Item name..." />
          <div className="ab-grid-2">
            <div>
              <label className="ab-label">Quantity</label>
              <input type="number" value={newItem.quantity} onChange={(e) => setNewItem(prev => ({ ...prev, quantity: Number(e.target.value) }))} className="ab-input" />
            </div>
            <div>
              <label className="ab-label">Price (₹)</label>
              <input type="number" value={newItem.price} onChange={(e) => setNewItem(prev => ({ ...prev, price: Number(e.target.value) }))} step="0.01" className="ab-input" />
            </div>
          </div>
          <label className="ab-label">Category</label>
          <select value={newItem.category} onChange={(e) => setNewItem(prev => ({ ...prev, category: e.target.value }))} className="ab-select">
            <option value="Appetizer">Appetizer</option>
            <option value="Main">Main Course</option>
            <option value="Dessert">Dessert</option>
            <option value="Beverage">Beverage</option>
          </select>
          <div className="ab-status-box">Total for this item: ₹{((newItem.price || 0) * (newItem.quantity || 1)).toFixed(2)}</div>
          <div className="modal-actions">
            <button onClick={handleAddItem} disabled={!newItem.name || !newItem.price} className="ab-btn ab-btn-purple">Add Item</button>
            <button onClick={() => setShowAddItemModal(false)} className="ab-btn ab-btn-gray">Cancel</button>
          </div>
        </div>
      </Modal>

      {/* TakeAway Delivery Modal */}
      <div className="wd-bottom-nav">
        <div className="wd-nav-buttons">
          <button onClick={() => setActiveScreen('dashboard')} className={activeScreen === 'dashboard' ? 'wd-nav-active' : 'wd-nav'}>
            <Home className="wd-icon-small" />
            <span>Dashboard</span>
          </button>
          <button onClick={() => setActiveScreen('pickup')} className={activeScreen === 'pickup' ? 'wd-nav-active' : 'wd-nav'}>
            <Utensils className="wd-icon-small" />
            <span>Dine-in</span>
            {myPickupQueue.length > 0 && (<span className="wd-badge-small">{myPickupQueue.length}</span>)}
          </button>
          <button onClick={() => setActiveScreen('takeaway')} className={activeScreen === 'takeaway' ? 'wd-nav-active' : 'wd-nav'}>
            <ShoppingBag className="wd-icon-small" />
            <span>Take Away</span>
            {takeAwayQueue.length > 0 && (<span className="wd-badge-small">{takeAwayQueue.length}</span>)}
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div
          className="wd-toast"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: toastType === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '15px',
            fontWeight: '500',
            animation: 'slideInRight 0.3s ease-out'
          }}
        >
          {toastType === 'success' ? (
            <CheckCircle size={24} />
          ) : (
            <AlertTriangle size={24} />
          )}
          {toastMessage}
        </div>
      )}

      {/* Payment QR Code Modal */}
      {showQrModal && paymentQrImageUrl && (
        <div
          onClick={() => setShowQrModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#1f1f1f',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#f3f4f6'
              }}>
                Payment QR Code
              </h2>
              <button
                onClick={() => setShowQrModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '24px'
                }}
              >
                ×
              </button>
            </div>

            {/* QR Image */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <img
                src={paymentQrImageUrl}
                alt="Payment QR Code"
                style={{
                  maxWidth: '100%',
                  maxHeight: '350px',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  console.error('Failed to load QR image');
                  e.target.style.display = 'none';
                }}
              />
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowQrModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#374151',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#374151'}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaiterDashboard;